import React, { useState, useEffect, useRef, useMemo } from "react";
import toast from "../components/common/toast";
import {
  inwardService,
  productService,
  binService,
  outwardService,
  putawayService,
  companyService,
} from "../services/api";
import { IoMdArrowDropdown } from "react-icons/io";
import { FiDownload } from "react-icons/fi";
import {
  Table,
  Badge,
  EditIcon,
  DeleteIcon,
  FormPopup,
  DeletePopUp,
  UploadPreviewPopUp,
  InwardDetailsModal,
} from "../components/common";
import * as XLSX from "xlsx";
import { parseFileToPreview } from "../utils/uploadPreviewParser";
import { downloadInwardUploadTemplate } from "../utils/downloadInwardUploadTemplate";
import { downloadInwardProductsTemplate } from "../utils/downloadInwardProductsTemplate";
import { required } from "zod/v4-mini";
import { parseISO, startOfMonth } from "date-fns";
import { DashboardDateRangePopup } from "./DashboardDemo";

function getProductUnitWeightKg(p) {
  if (!p) return 0;
  const w = Number(p.grossWeightAsPerPalletUom) || 0;
  return Number.isFinite(w) ? w : 0;
}

const pad2 = (n) => String(n).padStart(2, "0");

const formatYmdLocal = (dateObj) =>
  `${dateObj.getFullYear()}-${pad2(dateObj.getMonth() + 1)}-${pad2(
    dateObj.getDate()
  )}`;

const toDateInputValue = (value) => {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = new Date(raw);
  if (!Number.isFinite(d.getTime())) return "";
  return formatYmdLocal(d);
};

const toDateTimeLocalInputValue = (value) => {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) return raw.slice(0, 16);
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(raw))
    return raw.replace(" ", "T").slice(0, 16);
  const d = new Date(raw);
  if (!Number.isFinite(d.getTime())) return "";
  return `${formatYmdLocal(d)}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const dateInputToIsoUtc = (dateInput) => {
  const raw = String(dateInput || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return "";
  const [y, m, d] = raw.split("-").map((x) => Number(x));
  const utc = new Date(Date.UTC(y, (m || 1) - 1, d || 1, 0, 0, 0, 0));
  return utc.toISOString();
};

const normalizeYmd = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = new Date(raw);
  if (!Number.isFinite(d.getTime())) return "";
  return formatYmdLocal(d);
};

const parseFlexibleDateInput = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
    const [day, month, year] = raw.split("-");
    const normalized = `${year}-${month}-${day}`;
    const d = new Date(normalized);
    return Number.isFinite(d.getTime()) ? d : null;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const d = new Date(raw);
    return Number.isFinite(d.getTime()) ? d : null;
  }
  const d = new Date(raw);
  return Number.isFinite(d.getTime()) ? d : null;
};

function getInvoiceDatePickerBoundsLocal({ isEditMode = false, originalDate } = {}) {
  const formatDate = (d) => {
    const copy = new Date(d);
    copy.setHours(0, 0, 0, 0);
    return formatYmdLocal(copy);
  };
  const today = new Date();
  const todayYmd = formatDate(today);

  if (isEditMode) {
    if (originalDate && Number.isFinite(originalDate.getTime())) {
      return {
        min: formatDate(originalDate),
        max: todayYmd,
      };
    }
    // Edit mode safety: do not fall back to create min (today - 2).
    return {
      min: "",
      max: todayYmd,
    };
  }

  const todayStart = new Date(today);
  todayStart.setHours(0, 0, 0, 0);
  const minDay = new Date(todayStart);
  minDay.setDate(minDay.getDate() - 2);
  return { min: formatDate(minDay), max: todayYmd };
}
function isInvoiceDateAllowedLastThreeDays(invoiceDateStr) {
  const selectedYmd = normalizeYmd(invoiceDateStr);
  if (!selectedYmd) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minDay = new Date(today);
  minDay.setDate(minDay.getDate() - 2);
  const minYmd = formatYmdLocal(minDay);
  const maxYmd = formatYmdLocal(today);
  return selectedYmd >= minYmd && selectedYmd <= maxYmd;
}

const INWARD_BUSINESS_TZ = "Asia/Kolkata";

function isManufacturingDateNotFutureBusinessTz(mfgRaw) {
  if (mfgRaw == null || String(mfgRaw).trim() === "") return true;
  const d = new Date(mfgRaw);
  let mfgYmd;
  if (Number.isFinite(d.getTime())) {
    mfgYmd = new Intl.DateTimeFormat("en-CA", {
      timeZone: INWARD_BUSINESS_TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  } else {
    const s = String(mfgRaw).trim().split("T")[0];
    mfgYmd = /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
  }
  if (!mfgYmd) return true;
  const todayYmd = new Intl.DateTimeFormat("en-CA", {
    timeZone: INWARD_BUSINESS_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  return mfgYmd <= todayYmd;
}

const getStatusVariant = (status) => {
  // Status is always RECEIVED
  return "success";
};

const EXCEL_GRID_COLUMNS = [
  { key: "sku", label: "SKU" },
  { key: "productName", label: "Product Name" },
  { key: "invoiceQty", label: "Invoice Qty" },
  { key: "receivedQty", label: "Received Qty" },
  { key: "batchNumber", label: "Batch Number" },
  { key: "mfgDate", label: "Manufacturing Date" },
  { key: "qty", label: "Batch Qty" },
  { key: "innerUom", label: "UOM" },
  // { key: "outerUom", label: "Outer UOM" },
  { key: "weight", label: "Weight" },
  { key: "expDate", label: "Expiry Date" },
  // { key: "grossWeight", label: "Gross Weight" },
  // { key: "qtyInPallet", label: "Qty In Pallet" },
];

const createEmptyExcelRow = () => ({
  productName: "",
  sku: "",
  innerUom: "",
  // outerUom: "",
  weight: "",
  invoiceQty: "",
  receivedQty: "",
  // grossWeight: "",
  // qtyInPallet: "",
  batchNumber: "",
  mfgDate: "",
  expDate: "",
  qty: "",
});

const createInitialExcelRows = () =>
  Array.from({ length: 15 }, () => createEmptyExcelRow());

const normalizeDateOnly = (value) => {
  if (value == null || value === "") return "";
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return String(value).slice(0, 10);
  return d.toISOString().slice(0, 10);
};

const normalizeDateTimeMinute = (value) => {
  if (value == null || value === "") return "";
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return String(value).slice(0, 16);
  return d.toISOString().slice(0, 16);
};

const normalizeItemsForCompare = (items = []) =>
  (items || []).map((i) => ({
    productId: String(i?.productId?._id || i?.productId || ""),
    invoiceQuantity: Number(i?.invoiceQuantity || 0),
    receivedQuantity: Number(i?.receivedQuantity || 0),
  }));

const normalizeBatchesForCompare = (batches = []) =>
  (batches || []).map((b) => ({
    batchNumber: String(b?.batchNumber || ""),
    manufacturingDate: normalizeDateOnly(b?.manufacturingDate),
    expiryDate: normalizeDateOnly(b?.expiryDate),
    quantity: Number(b?.quantity || 0),
    condition: String(b?.condition || "GOOD"),
  }));

const buildProductTableFromTransaction = (transaction, productList = []) => {
  const formattedBatches = (transaction?.batches || []).map((batch) => ({
    batchNumber: batch.batchNumber ?? batch.batch_number ?? "",
    manufacturingDate: batch.manufacturingDate
      ? new Date(batch.manufacturingDate).toISOString().split("T")[0]
      : "",
    expiryDate: batch.expiryDate
      ? new Date(batch.expiryDate).toISOString().split("T")[0]
      : "",
    quantity: batch.quantity || 0,
    condition: batch.condition || "GOOD",
  }));

  let nextProductTable = {};
  if (transaction?.items && transaction.items.length > 0) {
    const byId = {};
    let batchOffset = 0;
    for (const it of transaction.items) {
      const productId = it.productId?._id || it.productId;
      const p = (productList || []).find(
        (pr) => String(pr._id) === String(productId)
      );
      const itemProductObj =
        it?.productId && typeof it.productId === "object" ? it.productId : null;
      const sku =
        String(p?.sku || itemProductObj?.sku || "").trim();
      const skuName = String(
        p?.skuName ||
          itemProductObj?.skuName ||
          itemProductObj?.description ||
          it?.productDescription ||
          ""
      ).trim();
      const uom = String(
        p?.innerUomConfig?.uom || p?.uom || itemProductObj?.uom || ""
      ).trim();
      const weight = Number(
        getProductUnitWeightKg(p) ||
          getProductUnitWeightKg(itemProductObj) ||
          itemProductObj?.weight ||
          0
      );
      const recQ = Number(it.receivedQuantity) || 0;
      const productBatches = [];
      let sum = 0;
      while (batchOffset < formattedBatches.length && sum < recQ) {
        const b = formattedBatches[batchOffset];
        const q = Number(b.quantity) || 0;
        productBatches.push(b);
        sum += q;
        batchOffset += 1;
      }
      byId[productId] = {
        selected: true,
        productDescription: it.productDescription ?? p?.description ?? "",
        sku,
        skuName,
        uom,
        weight: Number.isFinite(weight) ? weight : 0,
        invoiceQuantity: it.invoiceQuantity || 0,
        receivedQuantity: it.receivedQuantity || 0,
        batches: productBatches,
      };
    }
    (productList || []).forEach((p) => {
      if (!byId[p._id]) {
        byId[p._id] = {
          selected: false,
          productDescription: p.description || "",
          sku: String(p.sku || "").trim(),
          skuName: String(p.skuName || p.description || "").trim(),
          uom: String(p.innerUomConfig?.uom || p.uom || "").trim(),
          weight: Number(getProductUnitWeightKg(p) || 0),
          invoiceQuantity: 0,
          receivedQuantity: 0,
          batches: [],
        };
      }
    });
    nextProductTable = byId;
  } else {
    const firstId = transaction?.products?.[0]?._id || transaction?.products?.[0];
    const allBatches = formattedBatches;
    (productList || []).forEach((p) => {
      const selected = firstId && String(p._id) === String(firstId);
      nextProductTable[p._id] = selected
        ? {
            selected: true,
            productDescription: transaction?.productDescription || p.description || "",
            sku: String(p.sku || "").trim(),
            skuName: String(p.skuName || p.description || "").trim(),
            uom: String(p.innerUomConfig?.uom || p.uom || "").trim(),
            weight: Number(getProductUnitWeightKg(p) || 0),
            invoiceQuantity: transaction?.invoiceQuantity || 0,
            receivedQuantity: transaction?.receivedQuantity || 0,
            batches: [...allBatches],
          }
        : {
            selected: false,
            productDescription: p.description || "",
            sku: String(p.sku || "").trim(),
            skuName: String(p.skuName || p.description || "").trim(),
            uom: String(p.innerUomConfig?.uom || p.uom || "").trim(),
            weight: Number(getProductUnitWeightKg(p) || 0),
            invoiceQuantity: 0,
            receivedQuantity: 0,
            batches: [],
          };
    });
  }

  return nextProductTable;
};

const getOnboardedCompanyError = (dataOrError) => {
  const data =
    dataOrError?.responseData ||
    dataOrError?.response?.data ||
    dataOrError?.data ||
    dataOrError ||
    {};
  const rowErrors = Array.isArray(data?.data?.errors)
    ? data.data.errors
    : Array.isArray(data?.errors)
    ? data.errors
    : [];
  const firstRowMessage =
    rowErrors.find((e) => typeof e?.error === "string")?.error || "";
  const topMessage = typeof data?.message === "string" ? data.message : "";
  const message =
    firstRowMessage || topMessage || String(dataOrError?.message || "");
  const match = message.match(/Company '([^']+)' is not onboarded/i);
  if (!match) return null;
  return `Customer name '${match[1]}' is not onboarded.`;
};

function formatInwardExportDateYMD(value) {
  if (value == null || value === "") return "";

  let d;

  // Excel serial number (e.g. 46116)
  if (typeof value === "number") {
    const excelEpoch = new Date(1899, 11, 30);
    d = new Date(excelEpoch.getTime() + value * 86400000);
  }
  // Date object
  else if (value instanceof Date) {
    d = value;
  }
  // String handling - DD-MM-YYYY or DD/MM/YYYY
  else if (typeof value === "string") {
    const str = value.trim();

    // Excel number inside string like "04-04-2026 46116" or just number string
    const match = str.match(/(\d{5})$/);
    if (match) {
      const excelNum = Number(match[1]);
      const excelEpoch = new Date(1899, 11, 30);
      d = new Date(excelEpoch.getTime() + excelNum * 86400000);
    }
    // DD-MM-YYYY or DD/MM/YYYY
    else {
      const dm = str.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/);
      if (dm) {
        const [, dd, mm, yyyy] = dm;
        d = new Date(`${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`);
      } else {
        d = new Date(str);
      }
    }
  }

  if (!d || !Number.isFinite(d.getTime())) return "";

  // Force to Asia/Kolkata and get clean YYYY-MM-DD (prevents timezone shift)
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

export function InwardManagement() {
  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const filteredCompanies = (companies || []).filter(
    (c) => (c.contract?.onboardStatus || "pending") === "completed"
  );
  // OLD LOGIC (kept for reference):
  // const companyOptions = companies.map((c) => ({
  //   value: c._id,
  //   label: c.name,
  // }));
  const companyOptions = filteredCompanies.map((c) => ({
    value: c._id || c.id,
    label: `${c.companyCode} - ${c.name}`,
  }));
  const bulkCompanyOptions = filteredCompanies.map((c) => ({
    value: c._id || c.id,
    label: `${c.companyCode} - ${c.name}`,
  }));
  const [transactions, setTransactions] = useState([]);
  const [filterDate, setFilterDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isDateOpen, setIsDateOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [appliedFrom, setAppliedFrom] = useState("");
  const [appliedTo, setAppliedTo] = useState("");
  const [displayMonth, setDisplayMonth] = useState(() => new Date());
  const dateRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  // const UOM_OPTIONS = ["PCS", "KG", "L", "BOX", "PACK", "CASE", "UNIT"];
  const [formData, setFormData] = useState({
    _id: "",
    invoiceNumber: "",
    invoiceDate: new Date().toISOString().split("T")[0],
    companies: [],
    productTable: {}, // { [productId]: { selected, productDescription, invoiceQuantity, receivedQuantity, batches: [] } }
    vehicleNumber: "",
    vehicleTemperature: "",
    vehicleReportingDate: "",
    vehicleReportingTime: "",
    dockNumber: "",
    dockInTime: "",
    dockOutTime: "",
    // OLD: Location captured in active inward form
    // location: "",
    // uom: "",
    // outerUomQuantity: "",
    productTemperature: "",
    storageType: "",
    // chamberNumber: '',
  });

  // Company-bound products:
  // - Fetch products filtered by selected company (if any)
  // - Reset selected products/batches when company changes to avoid cross-company selection
  // - Do not clear productTable while editing (otherwise Edit load loses lines / uploaded data)
  useEffect(() => {
    fetchProducts();
    setFormData((prev) => {
      if (editingTransactionId) return prev;
      return { ...prev, productTable: {} };
    });
    if (!editingTransactionId) {
      setExcelRows(createInitialExcelRows());
      setExcelValidationErrors({});
    }
  }, [
    JSON.stringify((formData.companies || []).map(String).sort()),
    editingTransactionId,
  ]);
  const [loading, setLoading] = useState(false);
  const [bins, setBins] = useState([]);
  const [binCapacityList, setBinCapacityList] = useState([]);
  const [deletePopUpOpen, setDeletePopUpOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [outwardTransactions, setOutwardTransactions] = useState([]);
  const [putawayTasks, setPutawayTasks] = useState([]);
  const [uploadPreviewOpen, setUploadPreviewOpen] = useState(false);
  const [uploadPreviewFileName, setUploadPreviewFileName] = useState("");
  const [uploadPreviewHeaders, setUploadPreviewHeaders] = useState([]);
  const [uploadPreviewRows, setUploadPreviewRows] = useState([]);
  const [uploadPreviewSummary, setUploadPreviewSummary] = useState(null);
  const [uploadSelectedFile, setUploadSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [productUploadPreviewOpen, setProductUploadPreviewOpen] =
    useState(false);
  const [productUploadPreviewFileName, setProductUploadPreviewFileName] =
    useState("");
  const [productUploadPreviewHeaders, setProductUploadPreviewHeaders] =
    useState([]);
  const [productUploadPreviewRows, setProductUploadPreviewRows] = useState([]);
  const [productUploadSelectedFile, setProductUploadSelectedFile] =
    useState(null);
  const [productUploadLoading, setProductUploadLoading] = useState(false);
  const [addBatchForProductId, setAddBatchForProductId] = useState(null);
  const fileInputRef = useRef(null);
  const productTableFileRef = useRef(null);
  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewError, setViewError] = useState(null);
  const viewRowRef = useRef(null);
  const receivedQtyInputRefs = useRef({});
  const addBatchQtyInputRef = useRef(null);
  const latestEditTransactionRef = useRef(null);
  const originalEditSkuSetRef = useRef(new Set());
  const excelRowsChangedByUserRef = useRef(false);
  const [excelRows, setExcelRows] = useState(createInitialExcelRows);
  const [excelValidationErrors, setExcelValidationErrors] = useState({});

  const ensureExcelRowCount = (rows, minCount) => {
    const next = [...rows];
    while (next.length < minCount) next.push(createEmptyExcelRow());
    return next;
  };

  const getProductBySku = (skuValue) => {
    const normalizedSku = String(skuValue || "").trim().toLowerCase();
    if (!normalizedSku) return null;
    return (
      (products || []).find(
        (p) => String(p?.sku || "").trim().toLowerCase() === normalizedSku
      ) || null
    );
  };
  const enrichExcelRowsWithProductMeta = (rows, productList) => {
    return (rows || []).map((row) => {
      const rowProductId = String(row?.productId || "").trim();
      const product =
        (productList || []).find(
          (p) => rowProductId && String(p?._id) === rowProductId
        ) ||
        (productList || []).find(
          (p) =>
            String(p?.sku || "").trim().toLowerCase() ===
            String(row?.sku || "").trim().toLowerCase()
        );

      const productWeight = Number(product?.grossWeightAsPerPalletUom || 0);
      const existingWeight = Number(row?.weight || 0);
      const resolvedWeight =
        productWeight > 0 ? productWeight : existingWeight > 0 ? existingWeight : 0;

      return {
        ...row,
        productId: row?.productId || product?._id || "",
        sku: row?.sku || product?.sku || "",
        skuName:
          row?.skuName ||
          row?.productName ||
          product?.skuName ||
          product?.description ||
          "",
        innerUom:
          row?.innerUom ||
          row?.uom ||
          product?.innerUomConfig?.uom ||
          product?.uom ||
          "",
        uom:
          row?.uom ||
          row?.innerUom ||
          product?.innerUomConfig?.uom ||
          product?.uom ||
          "",
        weight: resolvedWeight > 0 ? String(resolvedWeight) : row?.weight || 0,
      };
    });
  };
  const isSameAsOriginalSkuInEdit = (skuValue) => {
    if (!formData?._id) return false;
    const normalizedSku = String(skuValue || "").trim().toLowerCase();
    if (!normalizedSku) return false;
    return originalEditSkuSetRef.current.has(normalizedSku);
  };

  const getExpectedInnerUom = (product) =>
    String(product?.innerUomConfig?.uom || product?.uom || "").trim();

  const isValidDate = (val) => {
   if (!val) return false;
 
   const patterns = [
     { regex: /^\d{4}[-\/]\d{2}[-\/]\d{2}$/, format: "yyyy-mm-dd" },
     { regex: /^\d{2}[-\/]\d{2}[-\/]\d{4}$/, format: "dd-mm-yyyy" },
     { regex: /^\d{4}[-\/]\d{2}[-\/]\d{2}$/, format: "yyyy-dd-mm" }, // handled separately
   ];
 
   let day, month, year;
 
   for (const p of patterns) {
     if (p.regex.test(val)) {
       const parts = val.split(/[-\/]/).map(Number);
 
       if (p.format === "yyyy-mm-dd") {
         [year, month, day] = parts;
       } 
       else if (p.format === "dd-mm-yyyy") {
         [day, month, year] = parts;
       } 
       else if (p.format === "yyyy-dd-mm") {
         [year, day, month] = parts;
       }
 
       const date = new Date(year, month - 1, day);
 
       return (
         date.getFullYear() === year &&
         date.getMonth() === month - 1 &&
         date.getDate() === day
       );
     }
   }
 
   return false;
 };

  const computeGridValidationErrors = (rows) => {
    const nextErrors = {};
    rows.forEach((row, rowIndex) => {
      const mfgDate = String(row?.mfgDate || "").trim();
      if (mfgDate && !isValidDate(mfgDate)) {
        nextErrors[`${rowIndex}-mfgDate`] = "Please enter a valid date";
      }
      const sku = String(row?.sku || "").trim();
      if (!sku) return;
      const product = getProductBySku(sku);
      if (!product) {
        if (isSameAsOriginalSkuInEdit(sku)) {
          return;
        }
        const companyName =
          (formData.companies || [])
            .map((id) => companies.find((c) => String(c._id || c.id) === String(id))?.name)
            .filter(Boolean)
            .join(", ") || "selected company";
        nextErrors[`${rowIndex}-sku`] = `Product SKU ${sku} not found in ${companyName}`;
        return;
      }
      const enteredInnerUom = String(row?.innerUom || "").trim();
      const expectedInnerUom = getExpectedInnerUom(product);
      if (
        enteredInnerUom &&
        expectedInnerUom &&
        enteredInnerUom.toLowerCase() !== expectedInnerUom.toLowerCase()
      ) {
        nextErrors[
          `${rowIndex}-innerUom`
        ] = `Invalid UOM for SKU ${sku}. Expected: ${expectedInnerUom}, Entered: ${enteredInnerUom}`;
      }
      const enteredProductName = String(row?.productName || "").trim();
      const expectedName = String(product?.skuName || product?.description || "").trim();
      if (
        enteredProductName &&
        expectedName &&
        enteredProductName.toLowerCase() !== expectedName.toLowerCase()
      ) {
        nextErrors[
          `${rowIndex}-productName`
        ] = `Invalid product name for SKU ${sku}. Expected: ${expectedName}`;
      }
    });
    return nextErrors;
  };

  const mapExcelRowsToProductTable = (rows) => {
    const nextProductTable = {};
    rows.forEach((row) => {
      const sku = String(row?.sku || "").trim();
      const product = getProductBySku(sku);
      if (!product) return;
      const productId = product._id;
      if (!nextProductTable[productId]) {
        nextProductTable[productId] = {
          selected: true,
          productDescription: String(
            row?.productName || product?.description || ""
          ).trim(),
          invoiceQuantity: 0,
          receivedQuantity: 0,
          batches: [],
        };
      }
      const invoiceQty = Number(row?.invoiceQty) || 0;
      const receivedQty = Number(row?.receivedQty) || 0;
      nextProductTable[productId].invoiceQuantity += invoiceQty;
      nextProductTable[productId].receivedQuantity += receivedQty;
      const batchQty = Number(row?.qty) || 0;
      if (
        String(row?.batchNumber || "").trim() ||
        String(row?.mfgDate || row?.manufacturingDate || "").trim() ||
        String(row?.expDate || "").trim() ||
        batchQty > 0
      ) {
        nextProductTable[productId].batches.push({
          batchNumber: String(row?.batchNumber || "").trim(),
          manufacturingDate: String(
            row?.mfgDate || row?.manufacturingDate || ""
          ).trim(),
          expiryDate: String(row?.expDate || "").trim(),
          quantity: batchQty,
          condition: "GOOD",
        });
      }
    });
    return nextProductTable;
  };

  const mapProductTableToExcelRows = (productTableObj, productList) => {
    const rows = [];
    const table = productTableObj || {};
    Object.entries(table).forEach(([productId, row]) => {
      if (!row?.selected) return;
      const product = (productList || []).find(
        (p) => String(p._id) === String(productId)
      );
      const batches = Array.isArray(row?.batches) && row.batches.length
        ? row.batches
        : [{ batchNumber: "", manufacturingDate: "", expiryDate: "", quantity: 0 }];
      batches.forEach((b) => {
        rows.push({
          productId: String(productId),
          productName:
            row?.skuName ||
            row?.productDescription ||
            product?.skuName ||
            product?.description ||
            "",
          sku: row?.sku || product?.sku || "",
          innerUom:
            row?.uom || product?.uom || product?.innerUomConfig?.uom || "",
          uom: row?.uom || product?.uom || "",
          grossWeightAsPerPalletUom: String(
            Number(row?.weight) || Number(product?.grossWeightAsPerPalletUom) || ""
          ),
          outerUom: product?.outerUom || "",
          weight: String(
            Number(row?.weight) || Number(product?.grossWeightAsPerPalletUom) || ""
          ),
          invoiceQty: String(row?.invoiceQuantity ?? ""),
          receivedQty: String(row?.receivedQuantity ?? ""),
          grossWeight: String(
            (Number(row?.receivedQuantity || 0) * Number(getProductUnitWeightKg(product) || 0)).toFixed(2)
          ),
          qtyInPallet: String(product?.qtyInPallet ?? ""),
          batchNumber: b?.batchNumber || "",
          mfgDate: b?.manufacturingDate || "",
          expDate: b?.expiryDate || "",
          qty: String(b?.quantity ?? ""),
        });
      });
    });
    return rows.length ? rows : [createEmptyExcelRow()];
  };

  // Normalize search once for smart, case-insensitive filtering
  const normalizedSearch = useMemo(
    () => (searchTerm ? searchTerm.trim().toLowerCase() : ""),
    [searchTerm]
  );

  // Frontend smart search over common visible fields, similar to Company/Product modules
  const filteredTransactions = useMemo(() => {
    if (!normalizedSearch) return transactions;
    const term = normalizedSearch;
    return (transactions || []).filter((t) => {
      const txId = String(t.transactionId ?? t._id ?? t.id ?? "").toLowerCase();
      const invoice = String(t.invoiceNumber ?? "").toLowerCase();
      const vehicle = String(t.vehicleNumber ?? "").toLowerCase();
      const status = String(t.status ?? "RECEIVED").toLowerCase();

      const product =
        t.productId || (Array.isArray(t.products) ? t.products[0] : null);
      const sku = String(
        product && typeof product === "object"
          ? product.sku ?? ""
          : product ?? ""
      ).toLowerCase();

      const companiesText = (t.companies || [])
        .map((c) =>
          `${c.name || ""} ${c.companyCode || ""}`.toString().toLowerCase()
        )
        .join(" ");

      return (
        txId.includes(term) ||
        invoice.includes(term) ||
        vehicle.includes(term) ||
        status.includes(term) ||
        sku.includes(term) ||
        companiesText.includes(term)
      );
    });
  }, [transactions, normalizedSearch]);

  const rangeLabel = useMemo(() => {
    const fmt = (v) => {
      if (!v) return "";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return v;
      const day = d.getDate();
      const mon = d.toLocaleString("en-GB", { month: "short" });
      return `${day} ${mon}`;
    };
    const a = fmt(dateFrom);
    const b = fmt(dateTo);
    if (!a && !b) return "Select Date";
    if (a && !b) return `${a} - ...`;
    if (!a && b) return `... - ${b}`;
    return `${a} - ${b}`;
  }, [dateFrom, dateTo]);

  const inwardColumns = [
    { key: "transactionId", label: "Transaction ID" },
    {
      key: "createdAt",
      label: "Created Date",
      render: (_, row) => {
        const d = row?.createdAt ? new Date(row.createdAt) : null;
        if (!d || !Number.isFinite(d.getTime())) return "—";
        return d.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" });
      },
    },
    {
      key: "invoiceNumber",
      label: "Customer Document Number",
      render: (v, row) => v || row?.invoiceNo || "—",
    },
    {
      key: "companies",
      label: "Customer Name",
      render: (_, row) => {
        const list = row.companies || [];
        if (!list.length) return "—";
        return list
          .map((c) => `${c.name} (${c.companyCode || "—"})`)
          .join(", ");
      },
    },
    {
      key: "productId",
      label: "Product",
      render: (_, row) => {
        const product = row.productId || row.products?.[0];
        if (!product) return "—";
        return product.sku || product;
      },
    },
    {
      key: "invoiceQuantity",
      label: "Invoice Qty",
      align: "right",
      render: (v, row) => {
        if (v != null && v !== "") return v;
        const items = Array.isArray(row?.items) ? row.items : [];
        const total = items.reduce(
          (sum, item) => sum + Number(item?.invoiceQuantity || 0),
          0
        );
        return total || "—";
      },
    },
    {
      key: "receivedQuantity",
      label: "Received Qty",
      align: "right",
      render: (v, row) => {
        if (v != null && v !== "") return v;
        const items = Array.isArray(row?.items) ? row.items : [];
        const total = items.reduce(
          (sum, item) => sum + Number(item?.receivedQuantity || 0),
          0
        );
        return total || "—";
      },
    },
    {
      key: "inwardAgeing",
      label: "Inward Ageing",
      align: "center",
      render: (_, row) => {
        const days = row.inwardAgeingDays;
        if (days != null) {
          return `${days} day${days !== 1 ? "s" : ""}`;
        }
        const invDate = row.invoiceDate ? new Date(row.invoiceDate) : null;
        if (!invDate || !Number.isFinite(invDate.getTime())) return "—";
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        invDate.setHours(0, 0, 0, 0);
        const d = Math.floor((today - invDate) / (1000 * 60 * 60 * 24));
        return `${d} day${d !== 1 ? "s" : ""}`;
      },
    },
    // {
    //   key: "vehicleNumber",
    //   label: "Vehicle",
    //   render: (v) => v || "—",
    // },
    {
      key: "status",
      label: "Status",
      // render: (val) => (
      //   <Badge variant={getStatusVariant(val)}>{val || "RECEIVED"}</Badge>
      // ),
      render: (val) => (
        <span
          style={{
            backgroundColor: "#E6F9F3", // light background
            color: "#0ACF97",
            border: "1px solid #0ACF97",
            padding: "4px 10px",
            borderRadius: "999px",
            fontSize: "12px",
            fontWeight: 600,
          }}
        >
          {val || "RECEIVED"}
        </span>
      ),
    },
  ];

  useEffect(() => {
    const onClick = (e) => {
      if (dateRef.current?.contains(e.target)) return;
      setIsDateOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // useEffect(() => {
  //   fetchProducts();
  //   fetchCompanies();
  //   fetchTransactions();
  //   fetchBins();
  //   fetchBinCapacityStatus();
  //   fetchOutwardTransactions();
  //   fetchPutawayTasks();
  // }, []);
  useEffect(() => {
    fetchProducts();
    fetchCompanies();
    fetchBins();
    fetchBinCapacityStatus();
    fetchOutwardTransactions();
    fetchPutawayTasks();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchTransactions(), 300);
    return () => clearTimeout(t);
  }, [searchTerm, filterDate, fromDate, toDate, appliedFrom, appliedTo]);

  const fetchBinCapacityStatus = async () => {
    try {
      const result = await binService.getBinCapacityStatus({
        status: "ACTIVE",
      });
      setBinCapacityList(result.data || []);
    } catch (error) {
      console.error("Error fetching bin capacity status:", error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const result = await companyService.getCompanies({ isActive: "true" });
      const list = result.data || [];
      // OLD LOGIC (kept for reference):
      // setCompanies(
      //   Array.isArray(list)
      //     ? list.filter(
      //         (c) =>
      //           String(c?.contract?.status || "").toLowerCase() === "completed"
      //       )
      //     : []
      // );
      setCompanies(
        Array.isArray(list) ? list : []
      );
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  // useEffect(() => {
  //   const t = setTimeout(() => fetchTransactions(), 300);
  //   return () => clearTimeout(t);
  // }, [searchTerm]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target))
        setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const fetchBins = async () => {
    try {
      const result = await binService.getBins();
      setBins(result.data || []);
    } catch (error) {
      console.error("Error fetching bins:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const isEditMode = Boolean(editingTransactionId || formData?._id);
      const selectedCompanyIds =
        Array.isArray(formData?.companies) && formData.companies.length > 0
          ? formData.companies.map(String).filter(Boolean)
          : [];
      if (selectedCompanyIds.length === 0 && !isEditMode) {
        setProducts([]);
        return;
      }
      if (selectedCompanyIds.length === 0 && isEditMode) return;
      const result = await productService.getProducts({
        companyIds: selectedCompanyIds.join(","),
      });
      setProducts(result.data || []);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterDate && String(filterDate).trim())
        params.date = String(filterDate).trim();
      if (fromDate && String(fromDate).trim())
        params.fromDate = String(fromDate).trim();
      if (toDate && String(toDate).trim())
        params.toDate = String(toDate).trim();
      if (appliedFrom && String(appliedFrom).trim())
        params.fromDate = String(appliedFrom).trim();
      if (appliedTo && String(appliedTo).trim())
        params.toDate = String(appliedTo).trim();
      // IMPORTANT: frontend smart search (like Product Master) should not depend on backend search matching,
      // otherwise partial/single-character search can fail depending on backend implementation.
      // Keep API contract unchanged; just don't pass `search` for listing.
      // OLD:
      // if (searchTerm && searchTerm.trim()) params.search = searchTerm.trim();
      const result = await inwardService.getInwardTransactions(params);
      setTransactions(result.data || []);
    } catch (error) {
      console.error("Error fetching inward transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  /** Query params for GET /reports/inward-download — aligned with list filters (see fetchTransactions) plus search. */
  const getInwardDownloadQueryParams = () => {
    const params = {};
    if (searchTerm && String(searchTerm).trim()) {
      params.search = String(searchTerm).trim();
    }
    // Single-day filter: list uses `date` on createdAt; report API uses invoiceDate fromDate/toDate
    if (filterDate && String(filterDate).trim()) {
      const d = String(filterDate).trim();
      params.fromDate = d;
      params.toDate = d;
    }
    if (fromDate && String(fromDate).trim()) {
      params.fromDate = String(fromDate).trim();
    }
    if (toDate && String(toDate).trim()) {
      params.toDate = String(toDate).trim();
    }
    if (appliedFrom && String(appliedFrom).trim()) {
      params.fromDate = String(appliedFrom).trim();
    }
    if (appliedTo && String(appliedTo).trim()) {
      params.toDate = String(appliedTo).trim();
    }
    // companyId / productId: not used by current list UI; add when list filters support them
    return params;
  };

  const handleApplyDateFilter = () => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
  };

  const fetchOutwardTransactions = async () => {
    try {
      const result = await outwardService.getOutwardTransactions({});
      setOutwardTransactions(result.data || []);
    } catch (error) {
      console.error("Error fetching outward transactions:", error);
    }
  };

  const fetchPutawayTasks = async () => {
    try {
      const result = await putawayService.getPutawayTasks({});
      setPutawayTasks(result.data || []);
    } catch (error) {
      console.error("Error fetching putaway tasks:", error);
    }
  };

  // OLD: Check if transaction can be edited/deleted (blocked as soon as any putaway existed)
  // const canEditOrDelete = (transaction) => {
  //   const batchNumbers = (transaction.batches || [])
  //     .map((b) => b.batchNumber)
  //     .filter(Boolean);
  //   if (batchNumbers.length === 0) return true;
  //
  //   // Check outward transactions
  //   const hasOutwardUsage = outwardTransactions.some((outwardTxn) => {
  //     if (outwardTxn.items && outwardTxn.items.length > 0) {
  //       return outwardTxn.items.some((item) => {
  //         if (item.batchDetails && item.batchDetails.length > 0) {
  //           return item.batchDetails.some((batchDetail) => {
  //             return batchNumbers.includes(
  //               String(batchDetail.batchNumber || "")
  //             );
  //           });
  //         }
  //         return false;
  //       });
  //     }
  //     return false;
  //   });
  //
  //   // Check putaway tasks
  //   const transactionId = transaction.transactionId || transaction._id;
  //   const hasPutawayUsage = putawayTasks.some((putaway) => {
  //     return (
  //       String(putaway.transactionId) === String(transactionId) &&
  //       String(putaway.transactionType) === "INWARD"
  //     );
  //   });
  //
  //   return !hasOutwardUsage && !hasPutawayUsage;
  // };

  // NEW: canEditOrDelete respects putaway status.
  // - Edit/delete allowed when:
  //   - there is no linked putaway task yet, and outward usage does not block it
  //   - all linked putaway tasks for this inward are still PENDING
  // - Edit/delete blocked when:
  //   - any linked putaway task status is not PENDING
  const canEditOrDelete = (transaction) => {
    const batchNumbers = (transaction.batches || [])
      .map((b) => b.batchNumber)
      .filter(Boolean);
    if (batchNumbers.length === 0) return true;

    // Outward protection remains unchanged
    const hasOutwardUsage = outwardTransactions.some((outwardTxn) => {
      if (outwardTxn.items && outwardTxn.items.length > 0) {
        return outwardTxn.items.some((item) => {
          if (item.batchDetails && item.batchDetails.length > 0) {
            return item.batchDetails.some((batchDetail) => {
              return batchNumbers.includes(
                String(batchDetail.batchNumber || "")
              );
            });
          }
          return false;
        });
      }
      return false;
    });

    const transactionId = transaction.transactionId || transaction._id;
    const linkedPutaways = putawayTasks.filter((putaway) => {
      return (
        String(putaway.transactionId) === String(transactionId) &&
        String(putaway.transactionType) === "INWARD"
      );
    });

    // If there are linked putaway tasks and any of them is not PENDING, block edit/delete
    const hasNonPendingPutaway = linkedPutaways.some((putaway) => {
      const status = String(putaway.status || "").toUpperCase();
      return status && status !== "PENDING";
    });

    if (hasNonPendingPutaway) {
      return false;
    }

    // If there is no linked putaway or all are PENDING, rely only on outward usage
    return !hasOutwardUsage;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]:
        name === "invoiceQuantity" || name === "receivedQuantity"
          ? parseInt(value, 10) || 0
          : value,
    });
  };

  const [currentBatch, setCurrentBatch] = useState({
    batchNumber: "",
    manufacturingDate: "",
    expiryDate: "",
    quantity: 0,
    condition: "GOOD",
    // binId: '',
  });

  const handleBatchChange = (e) => {
    const { name, value } = e.target;
    if (name === "manufacturingDate" && value) {
      if (!isManufacturingDateNotFutureBusinessTz(value)) {
        toast.error("Manufacturing date cannot be in the future.");
        return;
      }
    }
    let updatedBatch = {
      ...currentBatch,
      [name]: name === "quantity" ? parseInt(value, 10) || 0 : value,
    };
    // if (name === 'condition') {
    //   updatedBatch.binId = '';
    // }

    // When user changes manufacturingDate in add-batch row, derive expiry using product shelfLifeDays
    if (name === "manufacturingDate" && addBatchForProductId) {
      const product = (products || []).find(
        (p) => String(p._id) === String(addBatchForProductId)
      );
      const shelfLifeDays = Number(product?.shelfLifeDays || 0) || 0;
      const mfgDate = value ? new Date(value) : null;
      if (mfgDate && Number.isFinite(mfgDate.getTime()) && shelfLifeDays > 0) {
        const exp = new Date(mfgDate);
        exp.setDate(exp.getDate() + shelfLifeDays);
        const yyyy = exp.getFullYear();
        const mm = String(exp.getMonth() + 1).padStart(2, "0");
        const dd = String(exp.getDate()).padStart(2, "0");
        updatedBatch.expiryDate = `${yyyy}-${mm}-${dd}`;
      } else {
        updatedBatch.expiryDate = "";
      }
    }

    setCurrentBatch(updatedBatch);
  };

  const addBatch = () => {
    if (!addBatchForProductId) return;
    const productId = addBatchForProductId;
    const row = formData.productTable?.[productId] || {};
    const recQ = Number(row.receivedQuantity) || 0;
    const existingSum = (row.batches || []).reduce(
      (s, b) => s + (Number(b.quantity) || 0),
      0
    );
    const rawQtyStr = String(addBatchQtyInputRef.current?.value ?? "").trim();
    if (!rawQtyStr) {
      toast.error("Please enter batch quantity.");
      return;
    }

    const rawQtyNum = Number(rawQtyStr);
    if (!Number.isFinite(rawQtyNum)) {
      toast.error("Please enter batch quantity.");
      return;
    }

    if (rawQtyNum < 1) {
      toast.error("Batch quantity must be at least 1.");
      return;
    }

    if (existingSum + rawQtyNum > recQ) {
      toast.error("Total batch quantity cannot exceed received quantity.");
      return;
    }

    if (
      currentBatch.manufacturingDate &&
      !isManufacturingDateNotFutureBusinessTz(currentBatch.manufacturingDate)
    ) {
      toast.error("Manufacturing date cannot be in the future.");
      return;
    }
    setFormData((prev) => {
      const existing = prev.productTable?.[productId] || {};
      const batches = Array.isArray(existing.batches)
        ? [...existing.batches]
        : [];
      batches.push(currentBatch);
      return {
        ...prev,
        productTable: {
          ...prev.productTable,
          [productId]: { ...existing, batches },
        },
      };
    });
    setCurrentBatch({
      batchNumber: "",
      manufacturingDate: "",
      expiryDate: "",
      quantity: 0,
      condition: "GOOD",
      // binId: '',
    });
    setAddBatchForProductId(null);
  };

  const productTable = formData.productTable || {};
  const hasSelectedCompany =
    Array.isArray(formData.companies) && formData.companies.length > 0;
  const selectedProductRows = (products || []).filter((p) => {
    const row = productTable[p._id];
    return row && row.selected;
  });
  const receivedQty = selectedProductRows.reduce(
    (sum, p) => sum + (Number(productTable[p._id]?.receivedQuantity) || 0),
    0
  );
  const getAllBatches = () =>
    selectedProductRows.flatMap((p) => productTable[p._id]?.batches || []);
  const totalBatchQty = getAllBatches().reduce(
    (sum, b) => sum + (b.quantity || 0),
    0
  );
  const batchesMatchReceived = receivedQty > 0 && totalBatchQty === receivedQty;
  const perProductBatchesMatch = selectedProductRows.every((p) => {
    const row = productTable[p._id];
    const recQ = Number(row?.receivedQuantity) || 0;
    const batchSum = (row?.batches || []).reduce(
      (s, b) => s + (b.quantity || 0),
      0
    );
    return recQ === 0 || batchSum === recQ;
  });
  const showAddBatchFormForProduct = (productId) => {
    // OLD: in edit mode, add-batch form was blocked
    // if (editingTransactionId) return false;
    if (addBatchForProductId !== productId) return false;
    const row = productTable[productId];
    if (!row?.selected) return false;
    const recQ = Number(row.receivedQuantity) || 0;
    if (recQ <= 0) return false;
    const batchSum = (row.batches || []).reduce(
      (s, b) => s + (Number(b.quantity) || 0),
      0
    );
    return batchSum < recQ;
  };

  const setProductTableRow = (productId, patch) => {
    setFormData((prev) => {
      const existing = prev.productTable?.[productId] || {};
      const merged = { ...existing, ...patch };
      if (!Array.isArray(merged.batches))
        merged.batches = existing.batches ?? [];
      return {
        ...prev,
        productTable: {
          ...prev.productTable,
          [productId]: merged,
        },
      };
    });
  };
  const setProductTableSelectAll = (selected) => {
    setFormData((prev) => {
      const next = { ...prev.productTable };
      (products || []).forEach((p) => {
        const id = p._id;
        const existing = next[id] || {};
        next[id] = {
          ...existing,
          selected,
          productDescription:
            existing.productDescription ?? p.description ?? "",
          invoiceQuantity: existing.invoiceQuantity ?? 0,
          receivedQuantity: existing.receivedQuantity ?? 0,
          batches: existing.batches ?? [],
        };
      });
      return { ...prev, productTable: next };
    });
  };

  useEffect(() => {
    const nextErrors = computeGridValidationErrors(excelRows);
    setExcelValidationErrors(nextErrors);
    const shouldSyncFromExcel =
      !editingTransactionId || excelRowsChangedByUserRef.current;
    if (!shouldSyncFromExcel) return;
    const nextProductTable = mapExcelRowsToProductTable(excelRows);
    setFormData((prev) => ({
      ...prev,
      productTable: nextProductTable,
      companies: prev?.companies || [],
    }));
    excelRowsChangedByUserRef.current = false;
  }, [excelRows, products, editingTransactionId]);

  useEffect(() => {
    if (!editingTransactionId) return;
    if (excelRowsChangedByUserRef.current) return;
    if (!Array.isArray(products) || products.length === 0) return;
    if (!Array.isArray(excelRows) || excelRows.length === 0) return;
    const enrichedRows = enrichExcelRowsWithProductMeta(excelRows, products);
    const hasChanged =
      JSON.stringify(enrichedRows) !== JSON.stringify(excelRows);
    if (hasChanged) {
      setExcelRows(enrichedRows);
      return;
    }
    const tx = latestEditTransactionRef.current;
    if (!tx) return;

    setFormData((prev) => {
      const selectedRows = Object.values(prev?.productTable || {}).filter(
        (row) => row?.selected
      );
      if (!selectedRows.length) return prev;
      const hasMissingMetadata = selectedRows.some((row) => {
        const missingSku = !String(row?.sku || "").trim();
        const missingSkuName =
          !String(row?.skuName || row?.productDescription || "").trim();
        const missingUom = !String(row?.uom || "").trim();
        const missingWeight = !(Number(row?.weight) > 0);
        return missingSku || missingSkuName || missingUom || missingWeight;
      });
      if (!hasMissingMetadata) return prev;

      const nextProductTable = buildProductTableFromTransaction(tx, products);
      setExcelRows(mapProductTableToExcelRows(nextProductTable, products));
      return { ...prev, productTable: nextProductTable };
    });
  }, [editingTransactionId, products, excelRows]);

  const updateExcelCell = (rowIndex, columnKey, value) => {
    excelRowsChangedByUserRef.current = true;
    setExcelRows((prev) => {
      const rows = ensureExcelRowCount(prev, rowIndex + 1);
      const next = [...rows];
      const row = { ...(next[rowIndex] || createEmptyExcelRow()) };
      if (columnKey === "mfgDate") {
        setExcelValidationErrors((prevErrors) => {
          const key = `${rowIndex}-mfgDate`;
          if (value && !isValidDate(value)) {
            return { ...prevErrors, [key]: "Please enter a valid date" };
          }
          const nextErrors = { ...prevErrors };
          delete nextErrors[key];
          return nextErrors;
        });
      }
      row[columnKey] = value;
      if (
        columnKey === "sku" ||
        columnKey === "receivedQty" ||
        columnKey === "mfgDate"
      ) {
        const product = getProductBySku(row.sku);
        if (product) {
          // Autofill product-based fields only after SKU is entered/selected.
          row.innerUom = row.innerUom || String(product?.innerUomConfig?.uom || product?.uom || "");
          row.outerUom = row.outerUom || String(product?.outerUom || "");
          row.weight = row.weight || String(getProductUnitWeightKg(product) || "");
          row.qtyInPallet = row.qtyInPallet || String(product?.qtyInPallet ?? "");
          if (!String(row.productName || "").trim()) {
            row.productName = String(product?.skuName || product?.description || "");
          }

          const shelfLifeDays = Number(product?.shelfLifeDays || 0) || 0;
          if (columnKey === "mfgDate" && row.mfgDate && shelfLifeDays > 0) {
            const mfgDate = parseFlexibleDateInput(row.mfgDate);
            if (mfgDate && Number.isFinite(mfgDate.getTime())) {
              const exp = new Date(mfgDate);
              exp.setDate(exp.getDate() + shelfLifeDays);
              const yyyy = exp.getFullYear();
              const mm = String(exp.getMonth() + 1).padStart(2, "0");
              const dd = String(exp.getDate()).padStart(2, "0");
              row.expDate = `${yyyy}-${mm}-${dd}`;
            }
          }
        }
        const qty = Number(row.receivedQty) || 0;
        const weight = Number(row.weight) || 0;
        row.grossWeight =
          qty > 0 && weight > 0 ? String((qty * weight).toFixed(2)) : "";
      }
      next[rowIndex] = row;
      const last = next[next.length - 1];
      const hasLastData = Object.values(last || {}).some((v) =>
        String(v || "").trim()
      );
      if (hasLastData) next.push(createEmptyExcelRow());
      return next;
    });
  };

  const handleExcelGridPaste = (e, rowIndex, columnIndex) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text") || "";
    if (!text) return;
    const pastedRows = text
      .split(/\r?\n/)
      .filter((line) => line !== "");
    if (!pastedRows.length) return;
    excelRowsChangedByUserRef.current = true;
    setExcelRows((prev) => {
      const requiredCount = rowIndex + pastedRows.length;
      const next = ensureExcelRowCount(prev, requiredCount);
      pastedRows.forEach((rowText, rOffset) => {
        const cols = rowText.split("\t");
        cols.forEach((colValue, cOffset) => {
          const targetCol = EXCEL_GRID_COLUMNS[columnIndex + cOffset];
          if (!targetCol) return;
          const targetRowIndex = rowIndex + rOffset;
          const targetRow = {
            ...(next[targetRowIndex] || createEmptyExcelRow()),
          };
          targetRow[targetCol.key] = colValue;
          const product = getProductBySku(targetRow.sku);
          if (product) {
            targetRow.innerUom =
              targetRow.innerUom ||
              String(product?.innerUomConfig?.uom || product?.uom || "");
            targetRow.outerUom =
              targetRow.outerUom || String(product?.outerUom || "");
            targetRow.weight =
              targetRow.weight || String(getProductUnitWeightKg(product) || "");
            targetRow.qtyInPallet =
              targetRow.qtyInPallet || String(product?.qtyInPallet ?? "");
            if (!String(targetRow.productName || "").trim()) {
              targetRow.productName = String(
                product?.skuName || product?.description || ""
              );
            }
            const shelfLifeDays = Number(product?.shelfLifeDays || 0) || 0;
            if (targetRow.mfgDate && shelfLifeDays > 0) {
              const mfgDate = parseFlexibleDateInput(targetRow.mfgDate);
              if (mfgDate && Number.isFinite(mfgDate.getTime())) {
                const exp = new Date(mfgDate);
                exp.setDate(exp.getDate() + shelfLifeDays);
                const yyyy = exp.getFullYear();
                const mm = String(exp.getMonth() + 1).padStart(2, "0");
                const dd = String(exp.getDate()).padStart(2, "0");
                targetRow.expDate = `${yyyy}-${mm}-${dd}`;
              }
            }
          }
          const qty = Number(targetRow.receivedQty) || 0;
          const weight = Number(targetRow.weight) || 0;
          targetRow.grossWeight =
            qty > 0 && weight > 0 ? String((qty * weight).toFixed(2)) : "";
          next[targetRowIndex] = targetRow;
        });
      });
      const last = next[next.length - 1];
      const hasLastData = Object.values(last || {}).some((v) =>
        String(v || "").trim()
      );
      if (hasLastData) next.push(createEmptyExcelRow());
      return next;
    });
  };

  const toastOpt = {};

  const resetForm = () => {
    excelRowsChangedByUserRef.current = false;
    originalEditSkuSetRef.current = new Set();
    setFormData({
      _id: "",
      invoiceNumber: "",
      invoiceDate: new Date().toISOString().split("T")[0],
      companies: [],
      productTable: {},
      vehicleNumber: "",
      vehicleTemperature: "",
      vehicleReportingDate: "",
      vehicleReportingTime: "",
      dockNumber: "",
      dockInTime: "",
      dockOutTime: "",
      // location: "",
      // uom: "",
      // outerUomQuantity: "",
      productTemperature: "",
      storageType: "",
      // chamberNumber: '',
    });
    setEditingTransactionId(null);
    latestEditTransactionRef.current = null;
    setExcelRows(createInitialExcelRows());
    setExcelValidationErrors({});
  };

  const removeBatch = (productId, index) => {
    setFormData((prev) => {
      const existing = prev.productTable?.[productId] || {};
      const batches = (existing.batches || []).filter((_, i) => i !== index);
      return {
        ...prev,
        productTable: {
          ...prev.productTable,
          [productId]: { ...existing, batches },
        },
      };
    });
  };

  const updateBatchField = (productId, index, field, value) => {
    setFormData((prev) => {
      const existing = prev.productTable?.[productId] || {};
      const recQ = Number(existing.receivedQuantity) || 0;
      const otherSum = (existing.batches || []).reduce(
        (s, b, i) => (i === index ? s : s + (Number(b.quantity) || 0)),
        0
      );
      const maxQty = Math.max(0, recQ - otherSum);
      const batches = (existing.batches || []).map((b, i) => {
        if (i !== index) return b;
        let val = field === "quantity" ? parseInt(value, 10) || 0 : value;
        if (field === "quantity") {
          val = Math.min(Number(val), maxQty);
        }
        let updatedBatch = { ...b, [field]: val };
        // if (field === 'condition') updatedBatch.binId = '';

        // When editing manufacturingDate in edit mode, derive expiry using product shelfLifeDays
        if (field === "manufacturingDate") {
          const product = (products || []).find(
            (p) => String(p._id) === String(productId)
          );
          const shelfLifeDays = Number(product?.shelfLifeDays || 0) || 0;
          const mfgDate = val ? new Date(val) : null;
          if (
            mfgDate &&
            Number.isFinite(mfgDate.getTime()) &&
            shelfLifeDays > 0
          ) {
            const exp = new Date(mfgDate);
            exp.setDate(exp.getDate() + shelfLifeDays);
            const yyyy = exp.getFullYear();
            const mm = String(exp.getMonth() + 1).padStart(2, "0");
            const dd = String(exp.getDate()).padStart(2, "0");
            updatedBatch.expiryDate = `${yyyy}-${mm}-${dd}`;
          } else {
            updatedBatch.expiryDate = "";
          }
        }

        return updatedBatch;
      });
      return {
        ...prev,
        productTable: {
          ...prev.productTable,
          [productId]: { ...existing, batches },
        },
      };
    });
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    const dataToSubmit = e && e.preventDefault ? formData : e;
    // DEBUG (remove later)
    console.log("excelRows:", excelRows);
    const validProducts = (excelRows || []).filter(
      (row) =>
        (row?.productId || String(row?.sku || "").trim()) &&
        Number(row?.receivedQuantity ?? row?.receivedQty ?? 0) > 0
    );
    if (validProducts.length === 0) {
      toast.error("Add at least one product", toastOpt);
      return;
    }
    const payloadBatches = (excelRows || []).flatMap((row) => {
      if (Array.isArray(row?.batches) && row.batches.length > 0) {
        return row.batches
          .map((b) => ({
            batchNumber: b?.batchNumber ?? "",
            manufacturingDate: String(
              b?.manufacturingDate || b?.mfgDate || row?.mfgDate || ""
            ).trim(),
            expiryDate: b?.expiryDate ?? row?.expDate ?? "",
            quantity: Number(b?.quantity || 0),
            condition: b?.condition || "GOOD",
          }))
          .filter((b) => b.quantity > 0);
      }
      const qty = Number(row?.qty || 0);
      if (qty <= 0) return [];
      return [
        {
          batchNumber: String(row?.batchNumber || "").trim(),
          manufacturingDate: String(row?.mfgDate || "").trim(),
          expiryDate: String(row?.expDate || "").trim(),
          quantity: qty,
          condition: "GOOD",
        },
      ];
    });
    const hasValidBatch = payloadBatches.some(
      (b) => Number(b?.quantity || 0) > 0
    );
    if (!hasValidBatch) {
      toast.error("Please add at least one batch", toastOpt);
      return;
    }
    const invalidSkuRow = validProducts.find((row) => {
      const sku = String(row?.sku || "").trim();
      if (!sku) return false;
      const product = getProductBySku(sku);
      if (product) return false;
      return !isSameAsOriginalSkuInEdit(sku);
    });
    if (invalidSkuRow) {
      toast.error(
        `Invalid SKU in sheet: ${String(invalidSkuRow?.sku || "").trim()}`,
        toastOpt
      );
      return;
    }
    const itemsPayload = validProducts
      .map((row) => {
        const productId = row?.productId || getProductBySku(String(row?.sku || "").trim())?._id;
        return {
          productId: productId ? String(productId) : "",
          productDescription:
            String(
              row?.productDescription ||
                row?.productName ||
                ""
            ).trim(),
          invoiceQuantity: Number(
            row?.invoiceQuantity ?? row?.invoiceQty ?? 0
          ),
          receivedQuantity: Number(
            row?.receivedQuantity ?? row?.receivedQty ?? 0
          ),
          weight: Number(row?.weight || 0),
          grossWeightAsPerPalletUom: Number(
            row?.grossWeightAsPerPalletUom || 0
          ),
        };
      })
      .filter((it) => String(it.productId || "").trim());
    const finalProductList = itemsPayload;
    console.log("Final Products:", finalProductList);
    if (finalProductList.length === 0) {
      toast.error("Add at least one product", toastOpt);
      return;
    }
    for (const it of itemsPayload) {
      // NOTE: Product Description is deprecated from create UI (do not enforce in frontend).
      // if (
      //   !(it.productDescription != null && String(it.productDescription).trim())
      // ) {
      //   toast.error(
      //     "Product Description is required for each selected row",
      //     toastOpt
      //   );
      //   return;
      // }
      if (!it.invoiceQuantity || it.invoiceQuantity <= 0) {
        toast.error("Invoice Qty must be > 0 for each selected row", toastOpt);
        return;
      }
      if (!it.receivedQuantity || it.receivedQuantity <= 0) {
        toast.error("Received Qty must be > 0 for each selected row", toastOpt);
        return;
      }
      const weight =
        Number(it?.weight) ||
        Number(it?.grossWeightAsPerPalletUom) ||
        0;
      if (!Number.isFinite(weight) || weight <= 0) {
        toast.error("Product weight not found", toastOpt);
        return;
      }
      const prod = (products || []).find((p) => String(p._id) === String(it.productId));
      const hasInnerUom = !!(
        prod?.innerUomConfig?.uom && String(prod.innerUomConfig.uom).trim()
      );
      const innerUomQty = Number(prod?.innerUomQty || 0);
      if (hasInnerUom && (!Number.isFinite(innerUomQty) || innerUomQty < 1)) {
        toast.error(
          "Inner UOM Qty must be 1 or greater when Inner UOM is selected",
          toastOpt
        );
        return;
      }
      // const conversion = Number(prod?.conversionQtyPerPallet || 0);
      // if (!Number.isFinite(conversion) || conversion <= 0) {
      //   toast.error(
      //     "Conversion qty per pallet must be greater than 0",
      //     toastOpt
      //   );
      //   return;
      // }
      // OLD: receivedQty could not exceed invoiceQty
      // if (it.receivedQuantity > it.invoiceQuantity) {
      //   toast.error(
      //     "Received Qty cannot exceed Invoice Qty for each selected row",
      //     toastOpt
      //   );
      //   return;
      // }
    }

    const batchesInItemOrder = (excelRows || []).flatMap((row) => {
      const prod =
        row?.productId != null
          ? (products || []).find(
              (p) => String(p?._id) === String(row?.productId)
            )
          : getProductBySku(row?.sku);
      const productId = row?.productId || prod?._id;
      if (!productId) return [];
      if (Array.isArray(row?.batches) && row.batches.length > 0) {
        return row.batches
          .map((b, idx) => ({
            productId: String(productId),
            lineNumber: b?.lineNumber || idx + 1,
            batchNumber: b?.batchNumber ?? "",
            manufacturingDate: String(
              b?.manufacturingDate || b?.mfgDate || row?.mfgDate || ""
            ).trim(),
            expiryDate: b?.expiryDate ?? row?.expDate ?? "",
            quantity: Number(b?.quantity || 0),
            condition: b?.condition || "GOOD",
          }))
          .filter((b) => b.quantity > 0);
      }
      const qty = Number(row?.qty || 0);
      if (qty <= 0) return [];
      return [
        {
          productId: String(productId),
          lineNumber: 1,
          batchNumber: String(row?.batchNumber || "").trim(),
          manufacturingDate: String(
            row?.mfgDate || row?.manufacturingDate || ""
          ).trim(),
          expiryDate: String(row?.expDate || "").trim(),
          quantity: qty,
          condition: "GOOD",
        },
      ];
    });
    console.log("Final Batch Payload:", batchesInItemOrder);

    for (const b of batchesInItemOrder) {
      console.log("Validation manufacturingDate:", b?.manufacturingDate);
      if (
        b?.manufacturingDate &&
        !isManufacturingDateNotFutureBusinessTz(b.manufacturingDate)
      ) {
        toast.error("Manufacturing date cannot be in the future.", toastOpt);
        return;
      }
    }

    const selectedProductCompanyIdSet = new Set();
    itemsPayload.forEach((it) => {
      const prod = (products || []).find(
        (p) => String(p?._id) === String(it?.productId)
      );
      (prod?.companies || []).forEach((c) => {
        const cid = c?._id || c?.id || c;
        if (cid) selectedProductCompanyIdSet.add(String(cid));
      });
    });
    const isEditMode = !!dataToSubmit?._id;
    if (
      isEditMode &&
      (!dataToSubmit?.companies || dataToSubmit.companies.length === 0)
    ) {
      dataToSubmit.companies = latestEditTransactionRef.current?.companies || [];
    }
    const selectedCompanies =
      dataToSubmit?.companies || dataToSubmit?.companyIds || [];
    const originalCompanies =
      latestEditTransactionRef.current?.companies || [];
    if (
      isEditMode &&
      Array.isArray(selectedCompanies) &&
      selectedCompanies.length === 0 &&
      originalCompanies.length > 0
    ) {
      toast.error("Please select company", toastOpt);
      return;
    }
    if (!selectedCompanies.length) {
      toast.error("Please add at least one customer", toastOpt);
      return;
    }
    const selectedCompanyIds = Array.isArray(selectedCompanies)
      ? selectedCompanies.map((c) => String(c?.value || c)).filter(Boolean)
      : [];
    if (selectedCompanyIds.length > 0) {
      const hasNonOnboardedSelection = selectedCompanyIds.some((selectedCompany) => {
        const company = (companies || []).find(
          (c) => String(c._id || c.id) === String(selectedCompany)
        );
        return (company?.contract?.onboardStatus || "pending") !== "completed";
      });
      if (hasNonOnboardedSelection) {
        toast.error("Selected company is not onboarded");
        return;
      }
    }
    const filteredCompanyIds = selectedCompanyIds.filter((cid) =>
      selectedProductCompanyIdSet.has(String(cid))
    );

    const submitData = {
      ...dataToSubmit,
      companies: filteredCompanyIds,
      items: itemsPayload,
      batches: batchesInItemOrder,
    };
    delete submitData.company;
    delete submitData.products;
    delete submitData.productId;
    delete submitData.receivedQuantity;
    delete submitData.productTable;
    delete submitData._id;

    const productTemp = dataToSubmit?.productTemperature;
    const productTempNum =
      productTemp !== "" && productTemp != null ? Number(productTemp) : NaN;
    if (!Number.isFinite(productTempNum)) {
      toast.error("Product Temperature is required", toastOpt);
      return;
    }
    submitData.productTemperature = productTempNum;

    submitData.vehicleReportingDate =
      dataToSubmit.vehicleReportingDate &&
      String(dataToSubmit.vehicleReportingDate).trim() !== ""
        ? dataToSubmit.vehicleReportingDate
        : null;
    submitData.vehicleReportingTime =
      dataToSubmit.vehicleReportingTime != null &&
      String(dataToSubmit.vehicleReportingTime).trim() !== ""
        ? String(dataToSubmit.vehicleReportingTime).trim()
        : "";

    // Storage type UI disabled — do not persist from form (schema/backend may retain existing values)
    delete submitData.storageType;
    // if (
    //   dataToSubmit.storageType &&
    //   ["FROZEN", "CHILLED", "AMBIENT", "TROPICAL"].includes(
    //     dataToSubmit.storageType
    //   )
    // ) {
    //   submitData.storageType = dataToSubmit.storageType;
    //   // const chamber = String(dataToSubmit?.chamberNumber || '').trim();
    //   // if (!chamber) {
    //   //   toast.error('Chamber Number is required when Storage Type is selected', toastOpt);
    //   //   return;
    //   // }
    //   // submitData.chamberNumber = chamber;
    // }

    if (!submitData.invoiceNumber) {
      toast.error("Please enter customer document number", toastOpt);
      return;
    }
    const postingDateValue = submitData.postingDate ?? submitData.invoiceDate ?? "";
    if (!postingDateValue || String(postingDateValue).trim() === "") {
      toast.error("Please select posting date", toastOpt);
      return;
    }
    // DEBUG (remove later)
    console.log("EDIT MODE:", isEditMode);
    console.log("invoiceDate:", dataToSubmit?.invoiceDate);
    if (isEditMode) {
      const rawOriginal =
        latestEditTransactionRef.current?.invoiceDate ||
        latestEditTransactionRef.current?.postingDate;
      const rawSelected = dataToSubmit?.postingDate ?? dataToSubmit?.invoiceDate;
      const originalYmd = normalizeYmd(rawOriginal);
      const selectedYmd = normalizeYmd(rawSelected);
      const todayYmd = formatYmdLocal(new Date());
      if (!originalYmd || !selectedYmd) {
        toast.error("Please select posting date", toastOpt);
        return;
      }

      // DEBUG (remove later)
      console.log({ originalYmd, selectedYmd, todayYmd });

      if (selectedYmd < originalYmd || selectedYmd > todayYmd) {
        toast.error(
          "Posting date must be between original date and today",
          toastOpt
        );
        return;
      }
    } else if (!isInvoiceDateAllowedLastThreeDays(postingDateValue)) {
      toast.error(
        "Posting date can only be today, yesterday, or day before yesterday",
        toastOpt
      );
      return;
    }
    const invoiceDateIso = dateInputToIsoUtc(submitData.invoiceDate);
    if (!invoiceDateIso) {
      toast.error("Please select posting date", toastOpt);
      return;
    }
    submitData.invoiceDate = invoiceDateIso;
    // Handle Outer UOM Quantity: only when UOM is selected and quantity is > 0
    const hasUom = submitData.uom && String(submitData.uom).trim();
    const outerQtyNum =
      submitData.outerUomQuantity !== "" && submitData.outerUomQuantity != null
        ? Number(submitData.outerUomQuantity)
        : NaN;
    if (hasUom) {
      if (!Number.isNaN(outerQtyNum) && outerQtyNum > 0) {
        submitData.outerUomQuantity = outerQtyNum;
      } else {
        // If UOM is selected but quantity is invalid/<=0, drop the value
        submitData.outerUomQuantity = undefined;
      }
    } else {
      // If UOM not selected, ignore any outer UOM quantity
      delete submitData.outerUomQuantity;
    }

    const receivedTotal = (excelRows || []).reduce(
      (sum, row) =>
        sum + Number(row?.receivedQuantity ?? row?.receivedQty ?? 0),
      0
    );
    const batchTotal = (excelRows || []).reduce((sum, row) => {
      const nestedBatchTotal = (row?.batches || []).reduce(
        (bSum, b) => bSum + Number(b?.quantity || 0),
        0
      );
      return sum + nestedBatchTotal + Number(row?.qty || 0);
    }, 0);
    if (receivedTotal !== batchTotal) {
      toast.error(
        `Total batch qty must equal received qty (Received: ${receivedTotal}, Batch: ${batchTotal})`,
        toastOpt
      );
      return;
    }
    for (const it of itemsPayload) {
      const recQ = Number(it.receivedQuantity) || 0;
      const batchSum = (batchesInItemOrder || [])
        .filter((b) => String(b?.productId || "") === String(it.productId))
        .reduce((s, b) => s + (Number(b?.quantity) || 0), 0);
      if (recQ !== batchSum) {
        toast.error(
          `Product batch total must equal received quantity (${recQ}) for each selected product.`,
          toastOpt
        );
        return;
      }
    }

    /*
     * Capacity check / bin assignment is temporarily disabled.
     * Backend will no longer assign bins during inward creation.
     */
    // const byBinId = {};
    // for (const b of submitData.batches) {
    //   const id = b.binId ? String(b.binId) : '';
    //   if (!id) continue;
    //   byBinId[id] = (byBinId[id] || 0) + (Number(b.quantity) || 0);
    // }
    // for (const [binId, totalQty] of Object.entries(byBinId)) {
    //   const bin = binCapacityList.find((b) => String(b.binId || b._id) === binId);
    //   if (bin && totalQty > Number(bin.availableCapacity ?? 0)) {
    //     toast.error("Selected bin doesn't have enough available capacity. Choose another bin.", toastOpt);
    //     return;
    //   }
    // }

    try {
      setLoading(true);
      if (editingTransactionId) {
        const latestRes = await inwardService.getInwardTransactionById(
          editingTransactionId
        );
        const latestTx = latestRes?.data || latestRes || {};
        latestEditTransactionRef.current = latestTx;

        const patchData = {};
        const nextItems = normalizeItemsForCompare(submitData.items || []);
        const latestItems = normalizeItemsForCompare(latestTx.items || []);
        const nextBatches = normalizeBatchesForCompare(submitData.batches || []);
        const latestBatches = normalizeBatchesForCompare(latestTx.batches || []);
        const nextCompanies = (submitData.companies || []).map(String).sort();
        const latestCompanies = (latestTx.companies || [])
          .map((c) => String(c?._id || c?.id || c))
          .sort();

        if (String(submitData.invoiceNumber || "") !== String(latestTx.invoiceNumber || "")) {
          patchData.invoiceNumber = submitData.invoiceNumber;
        }
        if (
          normalizeDateOnly(submitData.invoiceDate) !==
          normalizeDateOnly(latestTx.invoiceDate)
        ) {
          patchData.invoiceDate = submitData.invoiceDate;
        }
        if (JSON.stringify(nextCompanies) !== JSON.stringify(latestCompanies)) {
          if (nextCompanies.length === 0 && latestCompanies.length > 0) {
            // user removed → already handled by validation above → do nothing
          } else {
            patchData.companies = submitData.companies;
          }
        }
        if (String(submitData.vehicleNumber || "") !== String(latestTx.vehicleNumber || "")) {
          patchData.vehicleNumber = submitData.vehicleNumber;
        }
        if (
          Number(submitData.vehicleTemperature ?? "") !==
          Number(latestTx.vehicleTemperature ?? "")
        ) {
          patchData.vehicleTemperature = submitData.vehicleTemperature;
        }
        if (
          Number(submitData.productTemperature ?? "") !==
          Number(latestTx.productTemperature ?? "")
        ) {
          patchData.productTemperature = submitData.productTemperature;
        }
        if (String(submitData.dockNumber || "") !== String(latestTx.dockNumber || "")) {
          patchData.dockNumber = submitData.dockNumber;
        }
        if (
          normalizeDateTimeMinute(submitData.dockInTime) !==
          normalizeDateTimeMinute(latestTx.dockInTime)
        ) {
          patchData.dockInTime = submitData.dockInTime;
        }
        if (
          normalizeDateTimeMinute(submitData.dockOutTime) !==
          normalizeDateTimeMinute(latestTx.dockOutTime)
        ) {
          patchData.dockOutTime = submitData.dockOutTime;
        }
        if (
          normalizeDateOnly(submitData.vehicleReportingDate) !==
          normalizeDateOnly(latestTx.vehicleReportingDate)
        ) {
          patchData.vehicleReportingDate = submitData.vehicleReportingDate;
        }
        if (
          String(submitData.vehicleReportingTime || "") !==
          String(latestTx.vehicleReportingTime || "")
        ) {
          patchData.vehicleReportingTime = submitData.vehicleReportingTime;
        }
        if (JSON.stringify(nextItems) !== JSON.stringify(latestItems)) {
          patchData.items = submitData.items;
        }
        if (JSON.stringify(nextBatches) !== JSON.stringify(latestBatches)) {
          patchData.batches = submitData.batches;
        }

        await inwardService.updateInwardTransaction(editingTransactionId, patchData);
        toast.success("Inward transaction updated successfully");
      } else {
        await inwardService.createInwardTransaction(submitData);
        toast.success("Inward transaction created successfully!");
      }
      resetForm();
      setShowForm(false);
      fetchTransactions();
    } catch (error) {
      console.error("Error saving inward transaction:", error);
      const errorMsg =
        error?.message ||
        (editingTransactionId
          ? "Failed to update inward transaction"
          : "Failed to create inward transaction");
      toast.error(`Error: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const isEditMode = !!formData?._id;
  const rawDate =
    latestEditTransactionRef.current?.invoiceDate ||
    latestEditTransactionRef.current?.postingDate ||
    formData?.invoiceDate;
  const originalDate = rawDate ? new Date(rawDate) : null;
  const invoiceDatePickerBounds = getInvoiceDatePickerBoundsLocal({
    isEditMode,
    originalDate,
  });

  const inwardFields = [
    // ROW 1 (5 fields)
    {
      name: "invoiceNumber",
      label: "Customer Document Number",
      type: "text",
      required: true,
      grid: "1",
      placeholder: "Enter customer document number",
    },
    {
      name: "invoiceDate",
      label: "Posting Date",
      type: "date",
      required: true,
      grid: "1",
      min: invoiceDatePickerBounds.min,
      max: invoiceDatePickerBounds.max,
    },
    {
      name: "companies",
      label: "Customer Name",
      type: "multiselect",
      required: true,
      placeholder: "-- Select Customer Name --",
      // For create inward customer selection
      options: companyOptions,
      grid: "1",
    },
    {
      name: "vehicleNumber",
      label: "Vehicle No",
      type: "text",
      grid: "1",
      placeholder: "Enter vehicle no",
    },
    {
      name: "vehicleTemperature",
      label: "Vehicle Temperature (°C)",
      type: "number",
      unit: "°C",
      strictNumeric: true,
      placeholder: "Enter temperature",
      grid: "1",
    },
    {
      name: "vehicleReportingDate",
      label: "Vehicle Reporting Date",
      type: "date",
      grid: "1",
    },
    {
      name: "vehicleReportingTime",
      label: "Vehicle Reporting Time",
      type: "time",
      grid: "1",
    },
    // ROW 2 (5 fields)
    {
      name: "dockNumber",
      label: "Dock Number",
      type: "text",
      placeholder: "Enter dock number",
      grid: "1",
    },
    {
      name: "dockInTime",
      label: "Dock In-Time",
      type: "datetime-local",
      grid: "1",
    },
    {
      name: "dockOutTime",
      label: "Dock Out-Time",
      type: "datetime-local",
      grid: "1",
    },
    // OLD: Location field in active inward form
    // {
    //   name: "location",
    //   label: "Location",
    //   type: "text",
    //   placeholder: "Enter Location",
    //   grid: "1",
    // },
    // {
    //   name: "uom",
    //   label: "UOM",
    //   type: "select",
    //   placeholder: "-- Select UOM --",
    //   options: UOM_OPTIONS.map((v) => ({ label: v, value: v })),
    //   grid: "1",
    // },
    // {
    //   type: "custom",
    //   grid: "1",
    //   render: () => {
    //     const disabled = !String(formData.uom || "").trim();
    //     return (
    //       <div className="col-span-1">
    //         <label className="form-popup-label mb-1.5 block">
    //           Outer UOM Quantity
    //         </label>
    //         <input
    //           type="number"
    //           min={0}
    //           value={formData.outerUomQuantity}
    //           onChange={(e) => {
    //             const val = e.target.value;
    //             setFormData((prev) => ({
    //               ...prev,
    //               outerUomQuantity: val,
    //             }));
    //           }}
    //           disabled={disabled}
    //           placeholder={
    //             disabled
    //               ? "Select UOM first"
    //               : "Enter Outer UOM Quantity (> 0)"
    //           }
    //           className={`w-full rounded-[10px] border border-[#D0D5DD] px-3 py-2 text-sm ${
    //             disabled
    //               ? "bg-gray-100 text-gray-500 cursor-not-allowed"
    //               : "bg-white text-gray-800"
    //           }`}
    //         />
    //       </div>
    //     );
    //   },
    // },
    {
      name: "productTemperature",
      label: "Product Temperature (°C)",
      type: "number",
      required: true,
      unit: "°C",
      strictNumeric: true,
      placeholder: "Enter temperature",
      grid: "1",
    },
    // {
    //   name: "storageType",
    //   label: "Storage Type",
    //   type: "select",
    //   placeholder: "-- Select Storage Type --",
    //   options: [
    //     { label: "Frozen", value: "FROZEN" },
    //     { label: "Chilled", value: "CHILLED" },
    //     { label: "Ambient", value: "AMBIENT" },
    //     { label: "Tropical", value: "TROPICAL" },
    //   ],
    //   grid: "1",
    // },
    // {
    //   type: 'custom',
    //   grid: '1',
    //   render: () => {
    //     const disabled = !String(formData.storageType || '').trim();
    //     return (
    //       // <div className="col-span-1">
    //       //   {/* <label className="form-popup-label mb-1.5 block">Chamber Number</label> */}
    //       //   <input
    //       //     type="text"
    //       //     value={formData.chamberNumber}
    //       //     onChange={(e) => setFormData((prev) => ({ ...prev, chamberNumber: e.target.value }))}
    //       //     placeholder={disabled ? 'Select Storage Type first' : 'Enter chamber number'}
    //       //     disabled={disabled}
    //       //     className={`w-full ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
    //       //   />
    //       // </div>
    //     );
    //   },
    // },
  ];

  const openView = async (row) => {
    viewRowRef.current = row;
    setViewOpen(true);
    setViewLoading(true);
    setViewError(null);
    try {
      const res = await inwardService.getInwardTransactionById(
        row._id || row.id
      );
      const transaction = res.data || res;
      setViewData(transaction);
    } catch (err) {
      console.error("Error fetching inward details:", err);
      setViewError(err?.message || "Failed to load transaction details");
      setViewData(null);
    } finally {
      setViewLoading(false);
    }
  };

  const handleEdit = async (row) => {
    try {
      const transactionDetails = await inwardService.getInwardTransactionById(
        row._id || row.id
      );
      const transaction = transactionDetails.data || transactionDetails;

      const txCompanyIds = (transaction.companies || [])
        .map((c) => c._id || c.id)
        .filter(Boolean);
      let productListForEdit = products || [];
      if (txCompanyIds.length > 0) {
        try {
          const pres = await productService.getProducts({
            companyIds: txCompanyIds.map(String).join(","),
          });
          productListForEdit = pres.data || [];
          setProducts(productListForEdit);
        } catch (err) {
          console.error("Error fetching products for edit:", err);
        }
      }

      latestEditTransactionRef.current = transaction;
      // OLD: productTable was a const from outer scope and reassigned/mutated here, causing
      // "Assignment to constant variable" at runtime when clicking Edit.
      // We keep the original code commented and use a new mutable variable instead.
      //
      // if (transaction.items && transaction.items.length > 0) {
      //   const byId = {};
      //   let batchOffset = 0;
      //   for (const it of transaction.items) {
      //     const productId = it.productId?._id || it.productId;
      //     const p = (products || []).find(
      //       (pr) => String(pr._id) === String(productId)
      //     );
      //     const recQ = Number(it.receivedQuantity) || 0;
      //     const productBatches = [];
      //     let sum = 0;
      //     while (batchOffset < formattedBatches.length && sum < recQ) {
      //       const b = formattedBatches[batchOffset];
      //       const q = Number(b.quantity) || 0;
      //       productBatches.push(b);
      //       sum += q;
      //       batchOffset += 1;
      //     }
      //     byId[productId] = {
      //       selected: true,
      //       productDescription: it.productDescription ?? p?.description ?? "",
      //       invoiceQuantity: it.invoiceQuantity || 0,
      //       receivedQuantity: it.receivedQuantity || 0,
      //       batches: productBatches,
      //     };
      //   }
      //   (products || []).forEach((p) => {
      //     if (!byId[p._id]) {
      //       byId[p._id] = {
      //         selected: false,
      //         productDescription: p.description || "",
      //         invoiceQuantity: 0,
      //         receivedQuantity: 0,
      //         batches: [],
      //       };
      //     }
      //   });
      //   productTable = byId;
      // } else {
      //   const firstId =
      //     transaction.products?.[0]?._id || transaction.products?.[0];
      //   const allBatches = formattedBatches;
      //   (products || []).forEach((p) => {
      //     const selected = firstId && String(p._id) === String(firstId);
      //     productTable[p._id] = selected
      //       ? {
      //           selected: true,
      //           productDescription:
      //             transaction.productDescription || p.description || "",
      //           invoiceQuantity: transaction.invoiceQuantity || 0,
      //           receivedQuantity: transaction.receivedQuantity || 0,
      //           batches: [...allBatches],
      //         }
      //       : {
      //           selected: false,
      //           productDescription: p.description || "",
      //           invoiceQuantity: 0,
      //           receivedQuantity: 0,
      //           batches: [],
      //         };
      //   });
      // }

      // NEW: use a mutable copy to build the product table for edit flow.
      const nextProductTable = buildProductTableFromTransaction(
        transaction,
        productListForEdit
      );

      const originalCompanyIds = (transaction.companies || []).map(
        (c) => c._id || c.id
      );
      const approvedIds = new Set(
        (filteredCompanies || []).map((c) => String(c._id || c.id))
      );
      let nextCompanyIds = originalCompanyIds;
      let mismatch = false;
      if ((companies || []).length && originalCompanyIds.length) {
        if (!originalCompanyIds.every((id) => approvedIds.has(String(id)))) {
          // Keep existing company IDs selected in edit mode.
          mismatch = true;
        }
      }

      setFormData({
        _id: transaction._id || row._id || row.id || "",
        invoiceNumber: transaction.invoiceNumber || "",
        invoiceDate: transaction.invoiceDate
          ? toDateInputValue(transaction.invoiceDate)
          : toDateInputValue(new Date()),
        companies:
          nextCompanyIds && nextCompanyIds.length > 0
            ? nextCompanyIds
            : (transaction?.companies || [])
                .map((c) => c?._id || c?.id || c)
                .filter(Boolean),
        productTable: nextProductTable,
        vehicleNumber: transaction.vehicleNumber || "",
        vehicleTemperature:
          transaction.vehicleTemperature != null
            ? transaction.vehicleTemperature
            : "",
        vehicleReportingDate: transaction.vehicleReportingDate
          ? toDateInputValue(transaction.vehicleReportingDate)
          : "",
        vehicleReportingTime:
          transaction.vehicleReportingTime != null &&
          String(transaction.vehicleReportingTime).trim() !== ""
            ? String(transaction.vehicleReportingTime).trim()
            : "",
        dockNumber: transaction.dockNumber || "",
        dockInTime: transaction.dockInTime
          ? toDateTimeLocalInputValue(transaction.dockInTime)
          : "",
        dockOutTime: transaction.dockOutTime
          ? toDateTimeLocalInputValue(transaction.dockOutTime)
          : "",
        location: transaction.location || "",
        // uom: transaction.uom != null && transaction.uom !== ""
        //   ? String(transaction.uom)
        //   : "",
        // outerUomQuantity:
        //   transaction.outerUomQuantity !== undefined &&
        //   transaction.outerUomQuantity !== null
        //     ? transaction.outerUomQuantity
        //     : "",
        productTemperature:
          transaction.productTemperature != null
            ? transaction.productTemperature
            : "",
        // storageType: transaction.storageType || "",
        // chamberNumber: transaction.chamberNumber || '',
      });
      excelRowsChangedByUserRef.current = false;
      const nextExcelRows = enrichExcelRowsWithProductMeta(
        mapProductTableToExcelRows(nextProductTable, productListForEdit),
        productListForEdit
      );
      originalEditSkuSetRef.current = new Set(
        (nextExcelRows || [])
          .map((r) => String(r?.sku || "").trim().toLowerCase())
          .filter(Boolean)
      );
      setExcelRows(nextExcelRows);
      setEditingTransactionId(transaction._id || row._id || row.id);
      setShowForm(true);
      if (mismatch) {
        toast.error(
          "Selected customer name is not approved. Please select an approved customer name.",
          toastOpt
        );
      }
    } catch (error) {
      console.error("Error fetching transaction details:", error);
      // OLD: generic toast
      // toast.error("Failed to load transaction details", toastOpt);
      // NEW: show backend message when available
      toast.error(
        error?.message || "Failed to load transaction details",
        toastOpt
      );
    }
  };

  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeletePopUpOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!rowToDelete) return;
    const id = rowToDelete._id || rowToDelete.id;
    try {
      setDeleteLoading(true);
      await inwardService.deleteInwardTransaction(id);
      setDeletePopUpOpen(false);
      setRowToDelete(null);
      fetchTransactions();
      // Use default toast container
      toast.success("Inward transaction deleted successfully");
    } catch (err) {
      console.error("Error deleting inward transaction:", err);
      // Use default toast container
      toast.error(err?.message || "Failed to delete inward transaction");
    } finally {
      setDeleteLoading(false);
    }
  };

  const resetUploadPreview = () => {
    setUploadPreviewOpen(false);
    setUploadPreviewFileName("");
    setUploadPreviewHeaders([]);
    setUploadPreviewRows([]);
    setUploadPreviewSummary(null);
    setUploadSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUploadInwardClick = () => {
    fileInputRef.current?.click();
  };

  const handleUploadFileSelect = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    try {
      const { headers, rows, allRows, totalRows, fileName } =
        await parseFileToPreview(file);

      const getCol = (r, keys) => {
        for (const k of keys) {
          if (r && Object.prototype.hasOwnProperty.call(r, k)) return r[k];
        }
        return "";
      };

      const addDerived = (r) => {
        const sku = String(
          getCol(r, [
            "Product SKU",
            "ProductSKU",
            "SKU",
            "productSKU",
            "sku",
          ]) || ""
        ).trim();
        const product = (products || []).find(
          (p) => String(p?.sku || "").trim() === sku
        );

        const qty = Number(
          getCol(r, [
            "Received Qty",
            "Received Quantity",
            "receivedQuantity",
            "receivedQty",
          ]) || 0
        );
        const weight = getProductUnitWeightKg(product);

        let grossWeight = 0;
        const qtyInPallet = Number(product?.qtyInPallet || 0);

        if (qty > 0 && weight > 0) {
          grossWeight = qty * weight;
        }

        // return {
        //   ...r,
        //   "Gross Weight": Number.isFinite(grossWeight)
        //     ? Number(grossWeight).toFixed(2)
        //     : "0.00",
        //   "Qty In Pallet": Number.isFinite(qtyInPallet)
        //     ? Number(qtyInPallet).toFixed(2)
        //     : "0.00",
        // };
      };

      const derivedAllRows = (
        Array.isArray(allRows) ? allRows : rows || []
      ).map(addDerived);
      const derivedPreview = (Array.isArray(rows) ? rows : []).map(addDerived);

      // Preview-only: filter out empty rows that cause 0,0,0 display in table
      const hasMeaningfulInwardUploadFields = (r) => {
        if (!r) return false;
        const v = (keys) => String(getCol(r, keys) ?? "").trim();
        const batchNumber = v(["Batch Number", "batchNumber", "batch_number"]);
        const manufacturingDate = v([
          "Manufacturing Date",
          "manufacturingDate",
          "mfgDate",
          "mfg_date",
        ]);
        const batchQuantity = v([
          "Batch Quantity",
          "batchQuantity",
          "batchQty",
          "batch_qty",
        ]);
        const invoiceQuantity = v([
          "Invoice Quantity",
          "invoiceQuantity",
          "invoiceQty",
          "invoice_qty",
        ]);
        const receivedQuantity = v([
          "Received Quantity",
          "Received Qty",
          "receivedQuantity",
          "receivedQty",
          "received_qty",
        ]);
        return !!(
          batchNumber ||
          manufacturingDate ||
          batchQuantity ||
          invoiceQuantity ||
          receivedQuantity
        );
      };

      const filteredAllRows = derivedAllRows.filter(
        hasMeaningfulInwardUploadFields
      );
      const filteredPreview = derivedPreview.filter(
        hasMeaningfulInwardUploadFields
      );

      // const nextHeaders = Array.isArray(headers) ? [...headers] : [];
      // if (!nextHeaders.includes("Gross Weight")) nextHeaders.push("Gross Weight");
      // if (!nextHeaders.includes("Qty In Pallet")) nextHeaders.push("Qty In Pallet");

      // const totalGrossWeight = filteredAllRows.reduce(
      //   (sum, r) => sum + Number(r?.["Gross Weight"] || 0),
      //   0
      // );

      setUploadPreviewFileName(fileName);
      // setUploadPreviewHeaders(nextHeaders);
      setUploadPreviewRows(filteredPreview);
      setUploadPreviewSummary({
        totalRows: Number(filteredAllRows.length ?? 0),
        // totalGrossWeight,
      });
      setUploadSelectedFile(file);
      setUploadPreviewOpen(true);
    } catch (err) {
      toast.error(err?.message || "Failed to read file");
    }
  };

  const handleUploadApply = async () => {
    if (!uploadSelectedFile) {
      resetUploadPreview();
      return;
    }
    setUploadLoading(true);
    try {
      const res = await inwardService.bulkUploadInwardExcel(uploadSelectedFile);
      const created = res?.data?.created ?? 0;
      const errors = res?.data?.errors || [];
      const errCount = errors.length;

      if (created > 0 && errCount === 0) {
        toast.success(
          `✅ Success! ${created} inward transaction(s) uploaded successfully.`,
          {
            autoClose: 4000,
          }
        );
      } else if (created > 0 && errCount > 0) {
        toast.success(
          `✅ ${created} inward transaction(s) uploaded successfully.`,
          {
            autoClose: 3000,
          }
        );
        // Show errors separately after a short delay - show exact backend error messages
        setTimeout(() => {
          const errorMessages = errors
            .slice(0, 5)
            .map((e) => {
              // Show exact backend error message - no modification
              const backendError = e.error || (typeof e === "string" ? e : "");
              return backendError;
            })
            .filter(Boolean);
          const errorText =
            errCount <= 5
              ? errorMessages.join(". ")
              : `${errorMessages.join(". ")}. ...and ${
                  errCount - 5
                } more error(s).`;
          // Show exact backend error message
          toast.error(errorText || "Some rows had errors", {
            autoClose: 6000,
          });
        }, 500);
      } else if (errCount > 0) {
        // All failed - show exact backend error messages
        const errorMessages = errors
          .slice(0, 5)
          .map((e) => {
            // Show exact backend error message - no modification
            const backendError = e.error || (typeof e === "string" ? e : "");
            return backendError;
          })
          .filter(Boolean);
        const errorText =
          errCount <= 5
            ? errorMessages.join(". ")
            : `${errorMessages.join(". ")}. ...and ${
                errCount - 5
              } more error(s).`;
        // Show exact backend error message
        toast.error(errorText || "Upload failed", {
          autoClose: 7000,
        });
      } else {
        toast.info(
          "ℹ️ No transactions found in file. Please check your file and try again.",
          {
            autoClose: 4000,
          }
        );
      }
      fetchTransactions();
      // Delay closing popup slightly so user can see toast
      setTimeout(() => {
        resetUploadPreview();
      }, 500);
    } catch (err) {
      // Extract backend error messages from error response data
      let errorMsg = err?.message || "Upload failed";
      const onboardedCompanyMessage = getOnboardedCompanyError(err);
      if (onboardedCompanyMessage) {
        errorMsg = onboardedCompanyMessage;
      }

      // Check if error has responseData attached (from API service)
      if (!onboardedCompanyMessage && err?.responseData) {
        const errorData = err.responseData;
        // Check for errors array in data.errors or errors
        if (
          errorData?.data?.errors &&
          Array.isArray(errorData.data.errors) &&
          errorData.data.errors.length > 0
        ) {
          // Extract exact backend error messages
          const backendErrors = errorData.data.errors
            .slice(0, 5)
            .map((e) => e.error || "")
            .filter(Boolean);
          if (backendErrors.length > 0) {
            errorMsg = backendErrors.join(". ");
          }
        } else if (
          errorData?.errors &&
          Array.isArray(errorData.errors) &&
          errorData.errors.length > 0
        ) {
          const backendErrors = errorData.errors
            .slice(0, 5)
            .map((e) => e.error || "")
            .filter(Boolean);
          if (backendErrors.length > 0) {
            errorMsg = backendErrors.join(". ");
          }
        }
      }

      toast.error(errorMsg, {
        autoClose: 5000,
      });
      // Delay closing popup slightly so user can see toast
      setTimeout(() => {
        resetUploadPreview();
      }, 500);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDownloadFormat = () => {
    downloadInwardUploadTemplate();
    toast.success(
      "Inward format downloaded. Fill and upload to add inward transactions."
    );
  };

  const handleExportProductsTemplate = () => {
    try {
      const rowsForExport =
        editingTransactionId
          ? mapProductTableToExcelRows(formData.productTable || {}, products || [])
          : null;

      if (
        editingTransactionId &&
        Array.isArray(rowsForExport) &&
        rowsForExport.length > 0 &&
        rowsForExport.some((r) => String(r?.sku || "").trim())
      ) {
        const headerLabels = EXCEL_GRID_COLUMNS.map((c) => c.label);
        const body = rowsForExport.map((r) =>
          EXCEL_GRID_COLUMNS.map((c) => r?.[c.key] ?? "")
        );
        const ws = XLSX.utils.aoa_to_sheet([headerLabels, ...body]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Products");
        const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([wbout], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `inward_${editingTransactionId}_products.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("Current inward product data exported successfully.", toastOpt);
        return;
      }

      if (!products || products.length === 0) {
        toast.error("No products available to export", toastOpt);
        return;
      }
      downloadInwardProductsTemplate(products);
      toast.success(
        "Products template downloaded. Fill editable columns and upload to pre-fill table.",
        toastOpt
      );
    } catch (err) {
      toast.error(err?.message || "Failed to export products", toastOpt);
    }
  };

  const resetProductUploadPreview = () => {
    setProductUploadPreviewOpen(false);
    setProductUploadPreviewFileName("");
    setProductUploadPreviewHeaders([]);
    setProductUploadPreviewRows([]);
    setProductUploadSelectedFile(null);
    if (productTableFileRef.current) productTableFileRef.current.value = "";
  };

  const handleProductUploadFileSelect = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    try {
      const { headers, rows, fileName } = await parseFileToPreview(file);
      setProductUploadPreviewFileName(fileName);
      setProductUploadPreviewHeaders(headers);
      setProductUploadPreviewRows(rows);
      setProductUploadSelectedFile(file);
      setProductUploadPreviewOpen(true);
    } catch (err) {
      toast.error(err?.message || "Failed to read file", toastOpt);
    }
  };

  const handleProductUploadApply = async () => {
    if (!productUploadSelectedFile) {
      resetProductUploadPreview();
      return;
    }
    setProductUploadLoading(true);
    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(new Uint8Array(ev.target.result));
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsArrayBuffer(productUploadSelectedFile);
      });
      const wb = XLSX.read(data, { type: "array" });
      const firstSheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(firstSheet, {
        header: 1,
        defval: "",
      });
      if (rows.length < 2) {
        toast.error(
          "File must have header row and at least one data row",
          toastOpt
        );
        return false;
      }
      const headers = (rows[0] || []).map((h) =>
        String(h).trim().toLowerCase().replace(/\s+/g, "")
      );
      const getCol = (names) => {
        for (const n of names) {
          // const i = headers.findIndex(
          //   (h) => h === n || h === String(n).toLowerCase()
          // );
          const i = headers.findIndex((h) =>
           h.replace(/\(.*?\)/g, "").trim() === n
         );
          if (i >= 0) return i;
        }
        return -1;
      };
      const skuCol = getCol(["productsku", "product_sku", "sku"]);
      const codeCol = getCol(["productcode", "product_code", "code"]);
      if (skuCol < 0 && codeCol < 0) {
        toast.error(
          "File must have productSKU or productCode column",
          toastOpt
        );
        return false;
      }
      const descCol = getCol(["productdescription", "product_description"]);
      const nameCol = getCol([
        "productname",
        "product_name",
        "skuname",
        "sku_name",
      ]);
      const tempCol = getCol(["producttemperature", "product_temperature"]);
      const invCol = getCol([
        "invoicequantity",
        "invoice_quantity",
        "invoiceqty",
        "invoice_qty",
      ]);
      const recCol = getCol([
        "receivedquantity",
        "received_quantity",
        "receivedqty",
        "received_qty",
      ]);
      const batchNumCol = getCol(["batchnumber", "batch_number"]);
      const mfgCol = getCol([
        "manufacturingdate",
        "manufacturing_date",
        "mfgdate",
      ]);
      // OLD: expiry column from product upload; expiry is now derived from Product Master shelfLifeDays
      // const expCol = getCol(["expirydate", "expiry_date", "expdate"]);
      const batchQtyCol = getCol([
        "batchquantity",
        "batch_quantity",
        "batchqty",
      ]);
      const conditionCol = getCol(["condition"]);
      const binCodeCol = getCol(["bincode", "bin_code"]);
      const uomCol = getCol(["uom"]);
const weightCol = getCol(["weight"]);

      const errors = [];
      const updates = {};
      const productBatchesMap = {}; // productId -> batches[]
      let firstTempFromFile = undefined;
      const binList = binCapacityList.length ? binCapacityList : bins;
      // const resolveBinCode = (code) => {
      //   const c = String(code ?? '').trim();
      //   if (!c) return '';
      //   const bin = binList.find((b) => (b.binCode || '').toString().trim() === c);
      //   return bin ? (bin.binId ?? bin._id) : '';
      // };

      for (let r = 1; r < rows.length; r++) {
        const row = rows[r] || [];
        const rawSku = skuCol >= 0 ? String(row[skuCol] ?? "").trim() : "";
        const rawCode = codeCol >= 0 ? String(row[codeCol] ?? "").trim() : "";
        const key = rawSku || rawCode;
        if (!key) continue;
        const normalize = (v) => String(v || "").trim().toLowerCase();

        // const product = (products || []).find((p) => {
        //   const sku = normalize(p?.sku);
        //   const code = normalize(p?.productCode ?? p?.sku);
        //   return (
        //     (rawSku && sku === normalize(rawSku)) ||
        //     (rawCode && code === normalize(rawCode))
        //   );
        // });
        const clean = (v) =>
         String(v || "")
           .trim()
           .toLowerCase()
           .replace(/[^a-z0-9]/gi, ""); // remove hyphen, spaces etc
       
       const product = (products || []).find((p) => {
         const sku = clean(p?.sku);
         const code = clean(p?.productCode ?? p?.sku);
       
         const rSku = clean(rawSku);
         const rCode = clean(rawCode);
       
         return (
           (rSku && (sku === rSku || sku.includes(rSku) || rSku.includes(sku))) ||
           (rCode && (code === rCode || code.includes(rCode) || rCode.includes(code)))
         );
       });
        let finalProduct = product;

        if (!finalProduct && editingTransactionId) {
          // fallback from existing inward data (edit mode)
          const existingItems = latestEditTransactionRef.current?.items || [];
          const matchedItem = existingItems.find((it) => {
            const sku = normalize(it?.productId?.sku || it?.sku);
            const code = normalize(it?.productId?.productCode || it?.productCode);
            return (
              (rawSku && sku === normalize(rawSku)) ||
              (rawCode && code === normalize(rawCode))
            );
          });

          if (matchedItem?.productId?._id) {
            const matchedId = String(matchedItem.productId._id);
            const masterProduct = (products || []).find(
              (p) => String(p?._id) === matchedId
            );

            finalProduct =
              masterProduct
                ? {
                    ...masterProduct,
                    _id: matchedItem.productId._id,
                    sku: matchedItem.productId.sku,
                    productCode: matchedItem.productId.productCode,
                    uom: masterProduct?.uom,
                    weight: masterProduct?.grossWeightAsPerPalletUom,
                  }
                : {
                    _id: matchedItem.productId._id,
                    sku: matchedItem.productId.sku,
                    productCode: matchedItem.productId.productCode,
                    uom: matchedItem.productId?.uom,
                    grossWeightAsPerPalletUom:
                      matchedItem.productId?.grossWeightAsPerPalletUom,
                  };
          }
        }

        if (!finalProduct) {
          errors.push({
            rowIndex: r + 1,
            error: `Unknown product SKU/Code: ${key}`,
          });
          continue;
        }
        const existingRow = formData?.productTable?.[finalProduct._id] || {};
        const invRaw = invCol >= 0 ? String(row[invCol] ?? "").trim() : "";
        const recRaw = recCol >= 0 ? String(row[recCol] ?? "").trim() : "";
        const hasInvoiceFromFile = invRaw !== "";
        const hasReceivedFromFile = recRaw !== "";
        const invQ = hasInvoiceFromFile
          ? Number(row[invCol])
          : Number(existingRow.invoiceQuantity || 0);
        const recQ = hasReceivedFromFile
          ? Number(row[recCol])
          : Number(existingRow.receivedQuantity || 0);

        if (hasInvoiceFromFile && (!Number.isFinite(invQ) || invQ < 1)) {
          errors.push({
            rowIndex: r + 1,
            error: `Invalid invoiceQuantity for ${key}`,
          });
          continue;
        }
        // OLD: blocked receivedQuantity when > invoiceQuantity
        // if (!Number.isFinite(recQ) || recQ < 1 || recQ > invQ) {
        //   errors.push({
        //     rowIndex: r + 1,
        //     error: `Invalid receivedQuantity for ${key} (must be > 0 and <= invoiceQuantity)`,
        //   });
        //   continue;
        // }
        // NEW: only require receivedQuantity to be numeric and > 0; allow recQ > invQ
        if (hasReceivedFromFile && (!Number.isFinite(recQ) || recQ < 1)) {
          errors.push({
            rowIndex: r + 1,
            error: `Invalid receivedQuantity for ${key} (must be > 0)`,
          });
          continue;
        }
        const productDescriptionRaw =
          descCol >= 0 ? String(row[descCol] ?? "").trim() : "";
        const productNameRaw =
          nameCol >= 0 ? String(row[nameCol] ?? "").trim() : "";
        const productDescription =
          productDescriptionRaw || productNameRaw || finalProduct?.skuName || "";
        const productTemperature =
          tempCol >= 0
            ? row[tempCol] !== "" && row[tempCol] != null
              ? Number(row[tempCol])
              : ""
            : "";
        // Product Description is optional. Keep fallback chain only; do not block upload if missing.
        if (
          firstTempFromFile === undefined &&
          Number.isFinite(Number(productTemperature))
        ) {
          firstTempFromFile = Number(productTemperature);
        }
        const masterProductRaw =
          (products || []).find(
            (p) => String(p?._id) === String(finalProduct?._id)
          ) ||
          (latestEditTransactionRef.current?.items || []).find(
            (it) =>
              String(it?.productId?._id || it?.productId || "") ===
              String(finalProduct?._id || "")
          )?.productId ||
          null;
        const masterProduct = masterProductRaw
          ? {
              ...masterProductRaw,
              grossWeightAsPerPalletUom:
                masterProductRaw?.grossWeightAsPerPalletUom ??
                masterProductRaw?.weight,
            }
          : null;
        // DEBUG (remove later)
        console.log("MASTER PRODUCT MAPPED", masterProduct);

        const resolvedUom = masterProduct?.uom;
        const resolvedWeight =
          Number(masterProduct?.grossWeightAsPerPalletUom) ||
          Number(finalProduct?.grossWeightAsPerPalletUom) ||
          0;

       if (!updates[finalProduct._id]) {
         updates[finalProduct._id] = {
           selected: true,
           sku: finalProduct.sku,
           productCode: finalProduct.productCode,
            uom: resolvedUom,
            weight: resolvedWeight,
         };
       }

        updates[finalProduct._id].uom = resolvedUom;
        updates[finalProduct._id].weight = resolvedWeight;
        updates[finalProduct._id].selected = true;
        if (productDescriptionRaw || productNameRaw) {
          updates[finalProduct._id].productDescription = productDescription;
        }
        if (hasInvoiceFromFile) {
          updates[finalProduct._id].invoiceQuantity = invQ;
        }
        if (hasReceivedFromFile) {
          updates[finalProduct._id].receivedQuantity = recQ;
        }
        const batchQty = batchQtyCol >= 0 ? Number(row[batchQtyCol]) : NaN;
        const hasBatchData =
          (batchNumCol >= 0 &&
            (row[batchNumCol] ?? "").toString().trim() !== "") ||
          (Number.isFinite(batchQty) && batchQty > 0) ||
          (mfgCol >= 0 && (row[mfgCol] ?? "").toString().trim() !== "");
        if (hasBatchData) {
          // const mfgVal = mfgCol >= 0 ? String(row[mfgCol] ?? "").trim() : "";
          let mfgVal = "";
          if (mfgCol >= 0) {
            const rawMfg = row[mfgCol];
            mfgVal = formatInwardExportDateYMD(rawMfg); // ← yeh best reuse hai
          }
          // OLD: expiry read from product upload row; now derived from shelfLifeDays
          // const expVal = expCol >= 0 ? String(row[expCol] ?? "").trim() : "";
          const qty = Number.isFinite(batchQty) && batchQty > 0 ? batchQty : 0;
          if (qty <= 0) {
            errors.push({
              rowIndex: r + 1,
              error: `Batch quantity must be > 0 for ${key} when batch columns are filled`,
            });
            continue;
          }
          const batchEntry = {
            batchNumber:
              batchNumCol >= 0 ? String(row[batchNumCol] ?? "").trim() : "",
            manufacturingDate: mfgVal || "",
            quantity: qty,
            condition:
              (conditionCol >= 0 &&
                (row[conditionCol] ?? "").toString().trim()) ||
              "GOOD",
            // binId: ... (if needed)
          };
          if (mfgVal) {
            const productForExpiry = products.find(
              (p) => p._id === finalProduct._id
            );
            const shelfLifeDays =
              Number(productForExpiry?.shelfLifeDays || 0) || 0;

            if (shelfLifeDays > 0) {
              const mfgDate = new Date(mfgVal);
              if (Number.isFinite(mfgDate.getTime())) {
                const exp = new Date(mfgDate);
                exp.setDate(exp.getDate() + shelfLifeDays);
                const yyyy = exp.getFullYear();
                const mm = String(exp.getMonth() + 1).padStart(2, "0");
                const dd = String(exp.getDate()).padStart(2, "0");
                batchEntry.expiryDate = `${yyyy}-${mm}-${dd}`;
              }
            }
          }
          if (!productBatchesMap[finalProduct._id])
            productBatchesMap[finalProduct._id] = [];
          productBatchesMap[finalProduct._id].push(batchEntry);
        }
      }
      Object.keys(updates).forEach((pid) => {
        if (Object.prototype.hasOwnProperty.call(productBatchesMap, pid)) {
          updates[pid].batches = productBatchesMap[pid] || [];
        }
      });
      if (errors.length > 0) {
        const msg = errors
          .slice(0, 5)
          .map((e) => `Row ${e.rowIndex}: ${e.error}`)
          .join("; ");
        toast.error(
          errors.length > 5 ? `${msg}... and ${errors.length - 5} more` : msg,
          { autoClose: 6000 }
        );
        return false;
      }
      setFormData((prev) => ({
        ...prev,
        productTable: Object.keys(updates).reduce((acc, pid) => {
          // const existing = acc[pid] || {
          //   selected: false,
          //   productDescription: "",
          //   invoiceQuantity: 0,
          //   receivedQuantity: 0,
          //   batches: [],
          // };
          const existing = acc[pid] || {
           selected: false,
           productDescription: "",
           invoiceQuantity: 0,
           receivedQuantity: 0,
           uom: "",
           weight: "",
           batches: [],
         };
          const patch = updates[pid] || {};
          acc[pid] = {
            ...existing,
            ...patch,
            batches:
              patch.batches !== undefined
                ? patch.batches
                : existing.batches || [],
          };
          return acc;
        }, { ...(prev.productTable || {}) }),
      }));
      excelRowsChangedByUserRef.current = true;
      setExcelRows(
        mapProductTableToExcelRows(
          { ...(formData.productTable || {}), ...updates },
          products || []
        )
      );
      if (firstTempFromFile !== undefined) {
        setFormData((prev) => ({
          ...prev,
          productTemperature:
            prev.productTemperature !== "" && prev.productTemperature != null
              ? prev.productTemperature
              : firstTempFromFile,
        }));
      }
      if (Object.keys(updates).length > 0) {
        excelRowsChangedByUserRef.current = true;
        toast.success(
          `${Object.keys(updates).length} product row(s) filled and selected`,
          toastOpt
        );
      }
      resetProductUploadPreview();
    } catch (err) {
      toast.error(err?.message || "Failed to parse file", toastOpt);
      return false;
    } finally {
      setProductUploadLoading(false);
    }
  };

  const handleDownloadGRN = async (row) => {
    try {
      const id = row._id || row.id;
      const blob = await inwardService.downloadInwardGRN(id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `GRN_${row.transactionId || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download GRN");
    }
  };

  const inwardActions = (row) => {
    const canEdit = canEditOrDelete(row);
    const actions = [];
    if (canEdit) {
      actions.push({
        label: "Edit",
        icon: EditIcon,
        onClick: (r) => handleEdit(r),
      });
      actions.push({
        label: "Delete",
        icon: DeleteIcon,
        onClick: (r) => handleDelete(r),
      });
    }
    actions.push({
      label: "Export GRN",
      icon: FiDownload,
      alwaysVisible: true,
      onClick: (r) => handleDownloadGRN(r),
    });
    return actions;
  };

  const handleExportInwardExcel = async () => {
    setExportLoading(true);
    try {
      const params = getInwardDownloadQueryParams();
      const blob = await inwardService.downloadInwardExcel(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `inward_report_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.message || "Failed to export inward excel");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportInwardGRN = () => {
    // ✅ UI only for now (later backend connect)
    toast.info("GRN export will be added soon.");
  };

  const inwardSkeletonRows = useMemo(
    () =>
      Array.from({ length: 8 }, (_, i) => ({
        __skeleton: true,
        _id: `inward-skeleton-${i}`,
      })),
    []
  );

  const inwardTableColumns = useMemo(() => {
    if (!loading) return inwardColumns;
    return inwardColumns.map((column) => ({
      ...column,
      render: () => (
        <div className="h-4 w-full max-w-[180px] rounded bg-gray-200 animate-pulse" />
      ),
    }));
  }, [loading, inwardColumns]);

  const inwardTableData = loading ? inwardSkeletonRows : filteredTransactions;

  return (
    <div className="page-container font-nunito">
      <div className="mb-6 w-full">
        <div
          className="flex items-center justify-between w-full flex-wrap gap-4"
          style={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #EEF1F5",
            borderRadius: 28,
            padding: "16px 24px",
            boxShadow: "0px 0px 4px 0px #959DA526",
          }}
        >
          <h1
            style={{
              fontFamily: "Nunito",
              fontSize: 18,
              fontWeight: 600,
              color: "#161616",
              margin: 0,
            }}
          >
            Inward Management
          </h1>

          <div className="flex items-center gap-[14px] flex-wrap">
            {/* OLD: simple From–To inputs (kept for reference; replaced by Dashboard popup) */}
            {/*
              <div className="flex items-center gap-3 flex-wrap">
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </div>
            */}

            {/* Dashboard: exact same popup range filter UI */}
            <div className="relative" ref={dateRef}>
              <div className="flex items-center gap-3">
                {/* <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Filter by Date :
                </span> */}

                <button
                  type="button"
                  onClick={() => {
                    const open = !isDateOpen;
                    if (open) {
                      setDisplayMonth(
                        startOfMonth(dateFrom ? parseISO(dateFrom) : new Date())
                      );
                    }
                    setIsDateOpen(open);
                  }}
                  className="inline-flex items-center rounded-xl border border-emerald-500/60 bg-white px-5 py-2 text-sm font-semibold text-emerald-700 shadow-sm hover:bg-emerald-50 active:scale-[0.99] transition"
                  style={{ minWidth: 180, justifyContent: "center" }}
                >
                  {rangeLabel}
                </button>
              </div>

              {isDateOpen && (
                <DashboardDateRangePopup
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  displayMonth={displayMonth}
                  setDisplayMonth={setDisplayMonth}
                  onSelectRange={(from, to) => {
                    setDateFrom(from);
                    setDateTo(to);
                  }}
                  onApply={() => {
                    handleApplyDateFilter();
                    setIsDateOpen(false);
                  }}
                  onClear={() => {
                    setDateFrom("");
                    setDateTo("");
                    setAppliedFrom("");
                    setAppliedTo("");
                  }}
                  onClose={() => setIsDateOpen(false)}
                />
              )}
            </div>

            <div className="relative" style={{ width: 320 }}>
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="h-4 w-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </span>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search"
                className="w-full rounded-[12px] border border-[#D0D5DD] bg-white pl-10 pr-4 text-sm text-gray-800 placeholder:text-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                style={{ height: 44 }}
              />
            </div>

            <button
              type="button"
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="shrink-0 rounded-[12px] bg-[#188742] text-white font-semibold transition hover:bg-[#15733a] active:scale-[0.98]"
              style={{ height: 44, paddingLeft: 18, paddingRight: 18 }}
            >
              Create Inward
            </button>

            <div className="relative shrink-0" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                className="flex items-center justify-center rounded-[12px] border-[1.5px] border-[#188742] bg-white text-[#188742] transition hover:bg-[rgba(24,135,66,0.06)]"
                style={{ width: 44, height: 44 }}
                aria-label="More options"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  className="w-5 h-5"
                >
                  <circle cx="12" cy="6" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="18" r="1.5" />
                </svg>
              </button>

              {menuOpen && (
                <div
                  className="absolute right-0 top-full z-50 mt-1 rounded-[12px] border border-[#EEF1F5] bg-white shadow-lg overflow-hidden"
                  style={{ width: 240, padding: 8 }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      handleDownloadFormat();
                      setMenuOpen(false);
                    }}
                    className="w-full text-left rounded-[10px] px-3 text-[#101828] font-medium transition hover:bg-[#F6F7FA]"
                    style={{ height: 40 }}
                  >
                    Download Format
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      handleUploadInwardClick();
                      setMenuOpen(false);
                    }}
                    className="w-full text-left rounded-[10px] px-3 text-[#101828] font-medium transition hover:bg-[#F6F7FA]"
                    style={{ height: 40 }}
                  >
                    Upload Inward
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      handleExportInwardExcel();
                      setMenuOpen(false);
                    }}
                    disabled={exportLoading}
                    className="w-full text-left rounded-[10px] px-3 text-[#101828] font-medium transition hover:bg-[#F6F7FA] disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ height: 40 }}
                  >
                    {exportLoading ? "Exporting..." : "Export Excel"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        accept=".xlsx,.xls,.csv"
        className="hidden"
        onChange={handleUploadFileSelect}
      />

      <UploadPreviewPopUp
        open={uploadPreviewOpen}
        onClose={resetUploadPreview}
        title="Upload Inward - File overview"
        fileName={uploadPreviewFileName}
        previewHeaders={uploadPreviewHeaders}
        previewRows={uploadPreviewRows}
        summary={uploadPreviewSummary}
        onApply={handleUploadApply}
        onCancel={resetUploadPreview}
        loading={uploadLoading}
      />
      <UploadPreviewPopUp
        open={productUploadPreviewOpen}
        onClose={resetProductUploadPreview}
        title="Upload Products Excel - File overview"
        fileName={productUploadPreviewFileName}
        previewHeaders={productUploadPreviewHeaders}
        previewRows={productUploadPreviewRows}
        onApply={handleProductUploadApply}
        onCancel={resetProductUploadPreview}
        loading={productUploadLoading}
      />
      <DeletePopUp
        open={deletePopUpOpen}
        onClose={() => {
          setDeletePopUpOpen(false);
          setRowToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        entityLabel="inward transaction"
      />
      <FormPopup
        open={showForm}
        title={
          editingTransactionId
            ? "Edit Inward Transaction"
            : "Create Inward Transaction"
        }
        onClose={() => {
          resetForm();
          setShowForm(false);
          if (productUploadPreviewOpen) resetProductUploadPreview();
        }}
        onSubmit={handleSubmit}
        submitDisabled={loading}
        submitText={
          editingTransactionId
            ? loading
              ? "Saving..."
              : "Update"
            : loading
            ? "Creating..."
            : "Create"
        }
        values={formData}
        setValues={setFormData}
        fields={inwardFields}
        width="xl"
        popupMaxWidth="1400px"
        popupHeight="94vh"
      >
        <div
          style={{
            width: "100%",
            border: "1px solid #E4E7EC",
            backgroundColor: "#FFFFFF",
            padding: 12,
            marginTop: 18,
          }}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "#101828",
                margin: 0,
              }}
            >
              Products
            </h3>
            <div className="flex flex-wrap items-center gap-[14px]">
              {/* <button
                type="button"
                onClick={() => setProductTableSelectAll(true)}
                className="border border-[#188742] bg-white px-3 py-1 text-[#188742] font-semibold transition hover:bg-[#188742]/5"
                style={{ height: 40, paddingLeft: 18, paddingRight: 18 }}
              >
                Select all
              </button> */}
              <button
                type="button"
                onClick={handleExportProductsTemplate}
                className="rounded-[10px] border border-[#188742] bg-white px-3 py-1 text-[#188742] font-semibold transition hover:bg-[#188742]/5"
                style={{ height: 40, paddingLeft: 18, paddingRight: 18 }}
              >
                Export CSV
              </button>
              <button
                type="button"
                onClick={() => productTableFileRef.current?.click()}
                disabled={!hasSelectedCompany}
                className="rounded-[10px] border border-[#188742] bg-white px-3 py-1 text-[#188742] font-semibold transition hover:bg-[#188742]/5"
                style={{ height: 40, paddingLeft: 18, paddingRight: 18, opacity: hasSelectedCompany ? 1 : 0.6, cursor: hasSelectedCompany ? "pointer" : "not-allowed" }}
              >
                Upload Product CSV
              </button>
            </div>
          </div>
          <input
            type="file"
            ref={productTableFileRef}
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleProductUploadFileSelect}
          />
          {productUploadPreviewOpen ? (
            <p className="text-sm text-gray-500 py-4">
              Previewing upload — use Apply or Cancel in the popup.
            </p>
          ) : (
            <div
              className="overflow-x-auto overflow-y-auto rounded-none"
              style={{
                border: "1px solid #D1D5DB",
              }}
            >
              <table className="w-full border border-black-300 border-collapse text-sm rounded-none" style={{ minWidth: 1800 }}>
                <thead className="sticky top-0 z-1">
                  <tr>
                    <th className="border border-gray-300 px-2 py-[4px] text-center font-semibold bg-gray-100" style={{ color: "#667085", minWidth: 52 }}>#</th>
                    {EXCEL_GRID_COLUMNS.map((col) => (
                      <th key={col.key} className="border border-gray-300 px-2 py-[4px] text-left font-semibold bg-gray-100" style={{ color: "#667085", minWidth: 120 }}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {excelRows.map((row, rowIndex) => (
                    <tr
                      key={`excel-row-${rowIndex}`}
                      style={{ background: "#fff" }}
                      title={!hasSelectedCompany ? "please select compnay first" : ""}
                    >
                      <td className="border border-gray-300 p-0 align-middle text-center text-xs text-gray-500">
                        {rowIndex + 1}
                      </td>
                      {EXCEL_GRID_COLUMNS.map((col, colIndex) => {
                        const err = excelValidationErrors[`${rowIndex}-${col.key}`];
                        return (
                          <td
                            key={`${rowIndex}-${col.key}`}
                            className="border border-gray-300 p-0 align-middle"
                            title={!hasSelectedCompany ? "please select compnay first" : ""}
                            style={{ minWidth: 120, cursor: !hasSelectedCompany ? "help" : "default" }}
                          >
                            <input
                              type={col.key === "expDate" ? "date" : (["weight", "invoiceQty", "receivedQty", "grossWeight", "qtyInPallet", "qty"].includes(col.key) ? "number" : "text")}
                              value={row[col.key] || ""}
                              disabled={!hasSelectedCompany || col.key === "expDate"}
                              title={!hasSelectedCompany ? "please select compnay first" : ""}
                              list={col.key === "sku" ? `sku-suggestions-${rowIndex}` : undefined}
                              onChange={(e) => updateExcelCell(rowIndex, col.key, e.target.value)}
                              onPaste={(e) => handleExcelGridPaste(e, rowIndex, colIndex)}
                              className="w-full h-full border-0 text-xs focus:outline-none focus:ring-0"
                              style={{
                                height: 26,
                                padding: "0 4px",
                                background: hasSelectedCompany && col.key !== "expDate" ? "#fff" : "#F9FAFB",
                                cursor: hasSelectedCompany && col.key !== "expDate" ? "text" : "not-allowed",
                              }}
                            />
                            {col.key === "sku" && (
                              <datalist id={`sku-suggestions-${rowIndex}`}>
                                {(products || [])
                                  .filter((p) => String(p?.sku || "").toLowerCase().includes(String(row?.sku || "").toLowerCase()))
                                  .slice(0, 20)
                                  .map((p) => (
                                    <option key={`${rowIndex}-${p._id}`} value={p.sku} />
                                  ))}
                              </datalist>
                            )}
                            {err ? (
                              <div className="px-2 pb-1 text-[11px]" style={{ color: "#D92D20" }}>
                                {err}
                              </div>
                            ) : null}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!productUploadPreviewOpen && hasSelectedCompany && products?.length === 0 && (
            <p className={`text-sm mt-2 ${hasSelectedCompany ? "text-gray-500" : "text-red-500"}`}>
              {hasSelectedCompany
                ? "No products available. Add products first."
                : "please select compnay first"}
            </p>
          )}
          {!productUploadPreviewOpen && selectedProductRows.length > 0 && (
            <p
              className="text-sm mt-2"
              style={{
                color:
                  batchesMatchReceived && perProductBatchesMatch
                    ? "#188742"
                    : "#344054",
              }}
            >
              {batchesMatchReceived && perProductBatchesMatch
                ? `Batches complete (${totalBatchQty}/${receivedQty}) — click Create to submit`
                : `Total batches: ${totalBatchQty}/${receivedQty}${
                    !perProductBatchesMatch
                      ? " — ensure each product's batch total equals its received qty"
                      : ""
                  }`}
            </p>
          )}
        </div>
      </FormPopup>
      <InwardDetailsModal
        open={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setViewData(null);
          setViewError(null);
        }}
        loading={viewLoading}
        error={viewError}
        data={viewData}
        onRetry={() => viewRowRef.current && openView(viewRowRef.current)}
      />
      <Table
        columns={inwardTableColumns}
        data={inwardTableData}
        loading={false}
        emptyMessage="No inward transactions found"
        actions={loading ? [] : inwardActions}
        onRowClick={loading ? undefined : (row) => openView(row)}
      />
    </div>
  );
}
